import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Target, ArrowDownToLine, ArrowUpFromLine, ChevronDown, ChevronRight, CornerDownRight, PencilLine } from 'lucide-react';
import { FilterDropdown, FilterOption, SwitchFilter, SHOW_CHILD_CLASSES_HINT, SHOW_CHILD_CLASSES_LABEL, type HowItWorksItem, type ReportPageConfig, WeeklyPlanningReportPage, Popover, SegmentedControl, InfoTooltip, TableStates, benchmarkDashboardCard, ChartCard, ChartSeries, salesAheadDashboardCard, type KpiItem, CELL_CAPTION_TEXT_CSS, DefaultRowLabel, HEADER_ROW_CSS, renderCellContent, rowLabelText, STICKY_LABEL_CELL_CSS } from '../kit/ui';
import { benchmarkIndex, benchmarkMonthEnd, costTotal, fmtCurrency, fmtDiffCurrency, formatBenchmark, formatIndex, formatPeriodLabel, gatedPeerValue, indexTone, isNum, monthYear, NOT_AT_COST_TOOLTIP, ON_ORDER_TIMING_CAVEAT, OTB_DEV_NOT_AT_COST, OTB_DEV_TOOLTIP, OTB_NOT_AT_COST, otbCoverageNoteProp, otbExclusionNote, otbWindowText, PEER_TEXT_NO_GROUP, PEER_TEXT_NOT_ENOUGH, peerText, periodNoun, retailTag, retailTooltip, RUNNING_OTB_RULE, valueOnBasis, basisSuffix, BasisValue, BENCHMARK_METRIC_BY_ID, benchmarkMonthNote, CellContext, CellFlags, comparableSales, CSV_FORMULA_RE, csvCell, EMPTY, epochMonthOfWeek, epochWeekEnd, epochWeekStart, fmtPct, formatWeekLabel, hasNoPlanLoaded, hasNoWeeklyPlan, isPositive, isTrendCell, isUsablePctBase, MetricDefinition, MONTHS, NO_WEEKLY_PLAN_LABEL, NO_WEEKLY_PLAN_TOOLTIP, noPlanLoadedCell, notAtCostCell, noWeeklyPlanCell, ON_ORDER_WEEKLY_CELL_CAVEAT, OTB_PERIOD_RULE_NOTE, otbLineOfPeriod, otbLines, otbThroughTitle, periodFullName, pickBasis, prorate, ProratedPlan, prorationTitle, runningTitle, salesLagText, signedPct, summaryOnBasis, TrendMethod, trendOf, trendTitle, TrendValues, weeksOfMonth, benchmarkForRows, chartRowsByClass, kpiLabel, kpiTotals, totalRows, type BenchmarkRowValue, csvFileName, csvNumber, otbSpanText, otbStartFor, PlanOtbMode, PlanOtbOptions, PlanOtbRow, defaultPlanOtbOptions, PLAN_OTB_HISTORY, FlatRow, HIERARCHY_BY_KEY, placeholderLoads, planEditCount, planEditKeyOf, PlanEdits, withPlanEdit, bomInventoryLabel, PLAN_EDIT_SPAN_KEY, PlanEditChanges, unitize, periodLabels } from '../kit/logic';
import type { WeeklyPlanningReportModel } from '../kit/useWeeklyPlanningReport';
import ReportModal from '../ReportModal.component';
import { AnyRow, BenchmarkMetricId, BenchmarkPeer, Cell, Grain, isVendorRow, OtbPeriodLine, PriceBasis } from '../../../interfaces/reports/WeeklyPlanning.interface';
import { cn } from '../../../lib/utils';
import { downloadCsv } from '../downloadCsv';

// The Plan & OTB matrix's cell logic (Plan 4 spec 7.2), shared by the matrix and its CSV so
// the two never disagree. Pure: no React.
//
// Decisions recorded here:
// - `metricValues(metric, cell, row, basis, method)` is the ONE place a metric's Plan and Act values are
// read. In a Sales Trend period the Sales Act is the cell's Sales Trend for the trend
// method (`comparableSales`, never sales ÷ Trend %), as `act` and `actRetail` alike (Sales Trend is
// derived from retail sales), so the matrix, its colour against the full-period plan, the Total and
// the CSV all follow it, and `trend` carries the trend values the cell was trended with (elapsed
// share, basis, note) for the matrix's "<Z>% elapsed" sub-line and its title (`trendCellTitle`:
// the share plus the kit's `trendTitle`, so the basis and "(requested: …)" read as in the other
// reports); no sub-line when the Act is null (a later-started trend period with no elapsed sales:
// Trend % 0, Sales Trend null). Other metrics and periods keep the actuals and carry `trend: null`.
// The CSV marks a trend period's header "<period> (trend)" (`csvPeriodHeader`). Hide future periods (once
// "Hide future actuals") keeps the current period, trended or not: it hides what sits in the Act cells AFTER it (the LY shown
// in place there with Show LY); the header mark stays.
// Cost basis: every Plan and Act value reads the kit's `basis.ts` rule, the same as the Weekly Action / Inventory Health columns: Inflow Act `inflowCost`,
// Inflow Plan `inflowCostPlan` (Monthly), Inventory Act `inventoryCostStart`, On Order `onOrderCost`, OTB `otbCost`;
// Sales, Markdowns, the Sales / Inventory / Markdowns plans and OTB + Dev have no cost figure and read "—" titled "not
// available at cost". A line's Total at cost is "—" when any shown cell is. The colour compares the RETAIL actual
// with the RETAIL plan (`actRetail` / `planRetail`) on either basis, so it never changes with the basis.
// Act inventory is the period's START inventory (`inventoryStart`), never `summary.inventory`: the kit's `inventoryOf` falls back to the summary, which is wrong per period.
// - In-progress periods: the Sales Act line shows the ACTUAL sales to date with the Trended sales on a second
// line (`LineCell.trended`; the colour still compares the Sales Trend with the full-period plan). The Total adds the trended
// value of an in-progress period instead of its actuals and is labelled "incl. trend" (`lineCells.includesTrend`). The CSV (
// review) keeps the Sales Act a plain number (the actual to date, Total = Σ actuals) and adds a "Sales Trended" line (Type
// "Trended": the trended sales in a trend period, the actual elsewhere, Total = the matrix's "incl. trend" Total). Inflows (Inflow) and Markdowns are measured, so in a
// trend period their colour compares the Act with the plan PRORATED by the days elapsed through `salesThrough` (kit `prorate`),
// and the Act cell reads "WTD" / "MTD" (`LineCell.proration`); where proration is impossible (no sales day, none elapsed: Tier C)
// the cell is uncoloured. Tier C's PROJECTED tag marks the Sales Act cell only (flagsOf below), never inventory / inflow / plans.
// - OTB + Dev is a second OTB line (`summary.otbDev`), right after OTB and shown whenever OTB is.
// - OTB by week / by month and Running OTB are two more lines in the OTB
// block, after OTB + Dev (`summary.otbByPeriod`, the backend's per-period split of the span OTB in the table's grain; retail
// only: "not available at cost"). A period's line shows in its own column (the week's in Weekly, the month's in Monthly), "—"
// for a period the window does not reach. Their Total covers the whole span, not the shown columns: Σ OTB by period = the OTB
// and the last Running OTB = the OTB (M1: running OTB governs spendability; the per-period figures are not independent budgets).
// - On Order, OTB and OTB + Dev are row-level span figures (`onOrderRetail` / `summary.otb` / `summary.otbDev`): they show in the
// TARGET period's column (the period the backend computes the summary for; the current period by
// default) and Total only, "—" elsewhere. OTB per period is not computed client-side.
// - Mode: Detailed = Sales + the selected Rows; Basic = Sales + OTB (when OTB is selected); OTB mode
// always shows the OTB line (the mode is the explicit choice).
// - Act is "—" after the current period; the current period always shows (Hide future periods no longer blanks it;
// it hides the LY shown in place in the periods after it). On Order is open orders, not an actual to date, so it is never blanked.
// - Colours (Act cells in period columns; ): Sales green at or above plan, red below; Inventory / Inflows green within
// ±10% of plan, amber within ±25%, red beyond (an inflow far under plan is as much a miss as one far over); Markdowns green at or
// below plan, amber up to +25% over it, red beyond (never red for being under plan). No colour when
// the plan is missing or not positive. The Total column is not coloured: its Act sums only the shown actuals while its
// Plan covers every shown period.
// - Totals: Σ over the shown periods' displayed values; Inventory = the CURRENT period's value on every line (Plan
// and Act at the same period, so the two compare; "—" when that period is not a shown column); On Order / OTB = the row value.
// - Plan cell ids (`salesPlan`, `inventoryPlan`, `inflowPlan`, `markdownsPlan`) are in the kit's
// PLAN_COLUMNS, so `renderCellContent` gives them the "no share" tag.
// - `lineCellNumber` (the CSV, plain numbers, blank where the matrix shows "—" or "No weekly plan") reads the line
// definition's `cell(...).value`, the figure the matrix renders through `renderCellContent`: one reading path.
// - Hierarchy views reach the matrix through `model.flatRows` (the kit's row tree): product rows are
// blocks with Act lines and blank Plan / OTB lines (plans stop at the class); at SKU grain their
// variants follow as blocks; placeholders load through `model.loadChildRows` (products in product
// views, SKUs otherwise). The first header is the view's `firstColumn`.
// - LY (W4, item 14; reworked after the 2026-10-20 review): Options > Show LY puts last year's figure INTO the Act line of Sales,
// Inflows and Markdowns, and of BOM inventory in Monthly only (Weekly has no LY BOM), not on a line of its own: a period whose Act is not
// shown (a future period) reads its LY in place, muted and marked "LY" (not under Hide future periods); the current period keeps its actual
// with "LY $X" small underneath (`LineCell.ly`); past periods show the actual with "+12.3% LY" small underneath (; where no
// other sub-line is there) and the variance against LY in their title (and, where no plan colours the cell, the same tone rule against LY).
// The Act Total stays actuals only. LY is retail only: at cost nothing is shown.
// The CSV keeps numeric "LY" lines (type 'ly', `linesFor(..., { lyLines: true })`), so a figure there is never actual-or-LY by column.
// - Period headers: weekly "Oct W3" (the week's place among the weeks starting in its month, as the
// backend's weeksStartingInMonth) over its date range "Oct 18–24"; monthly "Oct 2026" over
// "Oct 1–31". Hide period dates drops the date line. The CSV uses `formatPeriodLabel`.

// 'otbDev': the OTB + Dev line; it is not a Rows choice of its own but follows OTB's (the OTB block).
export type PlanOtbMetric = 'sales' | PlanOtbRow | 'otbDev' | 'otbPeriod' | 'otbRunning';
export type LineType = 'plan' | 'act' | 'ly';
export interface MatrixLine { metric: PlanOtbMetric; type: LineType }
export type Tone = 'good' | 'warn' | 'bad';

export const METRIC_ORDER: readonly PlanOtbMetric[] = ['sales', 'inventory', 'inflow', 'markdowns', 'onOrder', 'otb', 'otbDev', 'otbPeriod', 'otbRunning'];
export const METRIC_LABELS: Record<PlanOtbMetric, string> = { sales: 'Sales', inventory: 'BOM inventory', inflow: 'Inflows', markdowns: 'Markdowns', onOrder: 'On Order', otb: 'OTB', otbDev: 'OTB + Dev', otbPeriod: 'OTB by period', otbRunning: 'Running OTB' };
/** The metric's label in the table's grain: the per-period OTB line is "OTB by week" in Weekly, "OTB by month" in Monthly. */
export const metricLabel = (metric: PlanOtbMetric, grain: Grain | null | undefined): string =>
  metric === 'otbPeriod' && grain ? `OTB by ${grain === 'weekly' ? 'week' : 'month'}`
    // Wendi (2026-10-22): the start-of-period inventory line reads "BOW inventory" in Weekly.
    : metric === 'inventory' && grain ? bomInventoryLabel(grain) : METRIC_LABELS[metric];
export const TYPE_LABELS: Record<LineType, string> = { plan: 'Plan', act: 'Act', ly: 'LY' };
/**
 * what each OTB line is, for the info tooltip by its name in the matrix (the three are related: see the last sentences).
 *: there is no separate OTB line any more; the span's OTB is the OTB by period line's Total.
 */
export const otbLineTooltip = (metric: PlanOtbMetric, grain: Grain | null | undefined): string | null => {
  const period = grain === 'monthly' ? 'month' : 'week';
  switch (metric) {
    case 'otb': return 'Open to buy for the whole span: planned inflows over the span − received to date − on order due in the span. What is still open to spend.';
    case 'otbDev': return `The span's OTB (the OTB by ${period} Total) plus the deviation: OTB + (plan ${periodLabels(grain).bom} − actual ${periodLabels(grain).bom}) at the start of the span. Lower when you start the span over plan (carrying extra stock), higher when under.`;
    case 'otbPeriod': return `Open to buy by ${period}: each ${period}'s planned inflow − what was received and is on order due that ${period}. The ${period}s (plus Later) add up to the Total, the span's OTB (planned inflows − received − on order).`;
    case 'otbRunning': return `OTB by ${period} added up from the first ${period}: what is open to buy through that ${period}. Its last value is the span's OTB.`;
    default: return null;
  }
};
/**: the OTB by period line's Total is the span's OTB (the figure the dropped OTB line carried). */
export const OTB_PERIOD_TOTAL_TITLE = "The span's OTB (planned inflows − received − on order)";

/** The metrics that carry an LY line (W4); inventory only in Monthly (`linesFor`). */
export const LY_METRICS: readonly PlanOtbMetric[] = ['sales', 'inventory', 'inflow', 'markdowns'];
/** Buyer language: the OTB lines are computed over the span, not planned figures, so they carry no "Plan" type (the line's internal type stays 'plan'). */
const UNTYPED_METRICS: PlanOtbMetric[] = ['otb', 'otbDev', 'otbPeriod', 'otbRunning'];
export const typeLabelOf = (line: MatrixLine): string => (UNTYPED_METRICS.includes(line.metric) ? '' : TYPE_LABELS[line.type]);
/** A line's name: "Inflows Plan", "BOM inventory Act", "OTB" (no type on the OTB lines), "OTB by week" / "OTB by month" in the grain. */
export const lineName = (line: MatrixLine, grain?: Grain | null): string => [metricLabel(line.metric, grain), typeLabelOf(line)].filter(Boolean).join(' ');
export const TONE_CSS: Record<Tone, string> = { good: 'text-[#027A48]', warn: 'text-[#B54708]', bad: 'text-[#B42318]' };
const SPAN_METRICS: PlanOtbMetric[] = ['onOrder', 'otb', 'otbDev'];
/** the OTB and OTB + Dev are span figures: they sit in the Total column only (the per-period lines carry the periods). */
const TOTAL_ONLY_METRICS: PlanOtbMetric[] = ['otb', 'otbDev'];
export const isTotalOnly = (metric: PlanOtbMetric): boolean => TOTAL_ONLY_METRICS.includes(metric);
const PERIOD_OTB_METRICS: PlanOtbMetric[] = ['otbPeriod', 'otbRunning'];
export const isPeriodOtb = (metric: PlanOtbMetric): boolean => PERIOD_OTB_METRICS.includes(metric);
const SHOWN_BEFORE_TARGET = PLAN_OTB_HISTORY;
const TOLERANCE = 1e-9;

export const DEFAULT_OPTIONS: PlanOtbOptions = defaultPlanOtbOptions();
/** The view state's Plan & OTB options (the defaults when a state carries none). */
export const optionsOf = (m: WeeklyPlanningReportModel): PlanOtbOptions => m.view.state.planOtb ?? defaultPlanOtbOptions(m.view.state.grain);

/**
 * `plan` / `act` are on the displayed basis (null when not available at cost: `planAvailable` / `actAvailable` false);
 * `actRetail` / `planRetail` are the retail figures every colour compares; `trend` is the Sales Act's trend values in a trend
 * period (elapsed share, basis, note), null for every other metric and period.
 */
export interface MetricValues {
  plan: number | null; act: number | null; actRetail: number | null; planRetail: number | null;
  planAvailable: boolean; actAvailable: boolean; trend: TrendValues | null;
  /** Sales in a trend period: the Sales Trend on the displayed basis (null at cost); the Act is then the actual sales to date. */
  trended: number | null;
  /** Inflow / Markdowns in a trend period: how the plan was prorated for the colour (label null = not prorated / impossible). */
  proration: ProratedPlan | null;
}
const NONE: BasisValue = { value: null, basis: 'retail', available: true };
const values = (plan: BasisValue, act: BasisValue, planRetail: number | null | undefined, actRetail: number | null | undefined, trend: TrendValues | null = null, extra: { trended?: number | null; proration?: ProratedPlan | null } = {}): MetricValues => ({
  plan: plan.value, act: act.value, planRetail: isNum(planRetail) ? planRetail : null, actRetail: isNum(actRetail) ? actRetail : null,
  planAvailable: plan.available, actAvailable: act.available, trend, trended: extra.trended ?? null, proration: extra.proration ?? null,
});
/** Where a period sits for proration (the matrix passes it for every cell). */
/** `current`: the period in progress (ctx.currentPeriod).: Plan & OTB trends the CURRENT week / month only;
 * an earlier period the backend also marks as a trend period (its sales lag behind its end) shows its actuals, with no trend or prorated plan. */
export interface PeriodRef { period: number; grain: Grain; salesThrough?: string | null; current?: number }

/** Whether a period's cell is trended here: a trend cell in the current period (no `current` given: any trend cell, as before). */
const trendedHere = (cell: Cell | undefined, at?: PeriodRef): boolean => isTrendCell(cell) && (at?.current === undefined || at.period === at.current);

/**
 * W4: last year's figure for a metric in a period's cell (Sales, Inflows, Markdowns, BOM inventory), null where it has none. LY is
 * retail only: at cost it is not available (`available` false), the rule of the Sales and Markdowns Act.
 */
export const lyValue = (metric: PlanOtbMetric, cell: Cell | undefined, basis: PriceBasis): { value: number | null; available: boolean } => {
  const v = metric === 'sales' ? cell?.salesLy : metric === 'inflow' ? cell?.inflowLy : metric === 'markdowns' ? cell?.markdownsLy : metric === 'inventory' ? cell?.inventoryLy : null;
  return { value: basis === 'retail' && isNum(v) ? v : null, available: basis === 'retail' };
};

/** The one Plan / Act lookup per metric (see the header). `cell` is the period's cell; On Order and OTB read the row. */
export const metricValues = (metric: PlanOtbMetric, cell: Cell | undefined, row: AnyRow, basis: PriceBasis, method: TrendMethod = 'auto', at?: PeriodRef): MetricValues => {
  switch (metric) {
    case 'sales': {
      const trendedSales = trendedHere(cell, at) ? comparableSales(cell, method) : cell?.sales ?? null; // Sales Trend in the current period, actual sales otherwise (retail)
      // QA fix 8: a Tier C projected cell holds the month's projection as its sales and has no sales to date: the Act is that projection
      // (tagged PROJECTED, coloured against the full plan), with no second "Trended" line repeating it.
      if (!trendedHere(cell, at) || cell?.trendBasis === 'projected') return values(valueOnBasis(cell, 'salesPlan', basis), pickBasis(trendedSales, null, basis), cell?.salesPlan, trendedSales);
      // the Act is the actual to date; the Sales Trend rides beside it (and colours it, against the full plan).
      const actual = isNum(cell?.sales) && isNum(trendedSales) ? cell?.sales ?? null : null; // no Sales Trend (a period with no elapsed sales yet): no Act either
      const trendBasis = pickBasis(trendedSales, null, basis);
      return values(valueOnBasis(cell, 'salesPlan', basis), pickBasis(actual, null, basis), cell?.salesPlan, trendedSales, trendOf(cell, method), { trended: trendBasis.value });
    }
    case 'inventory':
      return values(valueOnBasis(cell, 'inventoryPlan', basis), valueOnBasis(cell, 'inventoryStart', basis), cell?.inventoryPlan, cell?.inventoryStart);
    case 'inflow':
    case 'markdowns': {
      const [planField, actField] = metric === 'inflow' ? (['inflowPlan', 'inflow'] as const) : (['markdownsPlan', 'markdowns'] as const);
      // in a trend period the colour compares with the plan prorated by days elapsed (null: impossible, uncoloured).
      // A trend cell outside the current period (its sales lag its end) keeps the full plan: no proration.
      const p = at && (!isTrendCell(cell) || trendedHere(cell, at)) ? prorate(cell?.[planField], cell, at.period, at.grain, at.salesThrough) : null;
      return values(valueOnBasis(cell, planField, basis), valueOnBasis(cell, actField, basis), p ? p.value : cell?.[planField], cell?.[actField], null, { proration: p });
    }
    case 'onOrder': {
      // The kit's On Order column rule (summary.onOrder, else the row's), so both reports show one figure.
      const oo = summaryOnBasis(row, 'onOrder', basis);
      return values(NONE, oo, null, row.summary?.onOrder ?? row.onOrderRetail);
    }
    case 'otb':
      return values(summaryOnBasis(row, 'otb', basis), NONE, row.summary?.otb, null);
    case 'otbDev':
      return values(summaryOnBasis(row, 'otbDev', basis), NONE, row.summary?.otbDev, null);
    default:
      return values(NONE, NONE, null, null);
  }
};

/**
 * whether the per-period OTB lines (OTB by week / by month, Running OTB) have anything to show: false when no class has a plan
 * (every judged row reads "No weekly plan", as a Weekly table of 4583 or 1913 does, or "No plan loaded"), so the lines are not shown empty.
 */
export const periodOtbAvailable = (payload: { rows: AnyRow[] } | null | undefined): boolean => {
  if (!payload) return true;
  const judged = payload.rows.filter((r) => r.summary);
  return judged.length === 0 || !judged.every((r) => hasNoWeeklyPlan(r) || hasNoPlanLoaded(r));
};

/**
 * The metric lines of one row block, in order, for the mode and Rows selection; `periodOtb` false drops the per-period OTB lines.
 * W4: with Show LY on, an LY line follows the Act line of Sales, Inflows and Markdowns, and of BOM inventory unless the grain is
 * Weekly (`grain`; Weekly has no LY BOM). Basic mode has the Sales LY only (its other rows are not shown).
 *: the 'otb' metric has no line of its own any more (the span's OTB is the OTB by period Total);
 * OTB + Dev, OTB by period and Running OTB still follow the OTB block (Rows "OTB", OTB mode).
 */
export const linesFor = (o: PlanOtbOptions, periodOtb = true, grain: Grain | null = null, { lyLines = false }: { lyLines?: boolean } = {}): MatrixLine[] => {
  const metrics = METRIC_ORDER.filter((metric) => {
    if (metric === 'otb') return false; // no OTB line: its figure is the OTB by period Total
    if (!periodOtb && PERIOD_OTB_METRICS.includes(metric)) return false;
    const m: PlanOtbMetric = metric === 'otbDev' || metric === 'otbPeriod' || metric === 'otbRunning' ? 'otb' : metric; // OTB + Dev and the per-month lines follow the OTB block
    if (o.mode === 'otb') return m === 'otb';
    if (m === 'sales') return true;
    if (!o.rows.includes(m)) return false;
    return o.mode === 'detailed' || m === 'otb';
  });
  return metrics.flatMap((metric): MatrixLine[] => {
    if (metric === 'onOrder') return [{ metric, type: 'act' }];
    if (metric === 'otb' || metric === 'otbDev' || metric === 'otbPeriod' || metric === 'otbRunning') return [{ metric, type: 'plan' }];
    const withLy = lyLines && lyInAct(o, metric, grain);
    return [{ metric, type: 'plan' }, { metric, type: 'act' }, ...(withLy ? [{ metric, type: 'ly' } as MatrixLine] : [])];
  });
};

/** Whether Show LY applies to this metric's Act line (Sales, Inflows, Markdowns; BOM inventory in Monthly only). */
export const lyInAct = (o: PlanOtbOptions, metric: PlanOtbMetric, grain: Grain | null | undefined): boolean =>
  o.showLy && LY_METRICS.includes(metric) && !(metric === 'inventory' && grain === 'weekly');

/** The payload periods from the target on; with Show previous, also the four just before it. */
export const displayPeriods = (periods: number[], targetPeriod: number, showPrevious: boolean): number[] => {
  const sorted = [...periods].sort((a, b) => a - b);
  const before = sorted.filter((p) => p < targetPeriod);
  return [...(showPrevious ? before.slice(-SHOWN_BEFORE_TARGET) : []), ...sorted.filter((p) => p >= targetPeriod)];
};

export const periodHeader = (period: number, grain: Grain): { main: string; sub: string } => {
  if (grain === 'monthly') {
    const year = 1970 + Math.floor(period / 12);
    const month = period % 12;
    const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return { main: formatPeriodLabel(period, grain), sub: `${MONTHS[month]} 1–${days}` };
  }
  const month = epochMonthOfWeek(period);
  const n = weeksOfMonth(month).indexOf(period) + 1;
  return { main: `${MONTHS[month % 12]} W${n}`, sub: formatWeekLabel(period, { withYear: false }) };
};

/** The OTB line a period column carries (the period's own, in the table's grain), or null: no lines, a period outside the span. */
export const periodOtbLine = (row: AnyRow, period: number, _ctx: CellContext): OtbPeriodLine | null => otbLineOfPeriod(row.summary, period);
/** Whole-span Total of a per-period line: Σ OTB by period, or the last Running OTB (both the row's OTB). */
const periodOtbTotal = (row: AnyRow, metric: PlanOtbMetric): number | null => {
  const lines = otbLines(row.summary);
  if (!lines) return null;
  return metric === 'otbRunning' ? lines[lines.length - 1].running : lines.reduce((t, l) => t + l.otb, 0);
};
const periodOtbValue = (row: AnyRow, metric: PlanOtbMetric, line: OtbPeriodLine | null): number | null => (line ? (metric === 'otbRunning' ? line.running : line.otb) : null);

/**
 * the "Later" column. A span period (week or month) whose line no period column carries (the window ends before
 * the span does) is held here, so the columns plus Later tie to the Total: OTB by period = Σ of the later periods, Running OTB = the
 * running figure through the last later period (the whole span's OTB). Null when every period has a column, at cost, or without lines.
 */
export const laterOtb = (row: AnyRow, metric: PlanOtbMetric, periods: number[], ctx: CellContext): { value: number | null; periods: number[] } | null => {
  const lines = otbLines(row.summary);
  if (!lines || ctx.basis === 'cost') return null;
  const carried = new Set(periods.map((p) => periodOtbLine(row, p, ctx)?.period).filter(isNum));
  const later = lines.filter((l) => !carried.has(l.period));
  if (later.length === 0) return null;
  const value = metric === 'otbRunning' ? later[later.length - 1].running : later.reduce((t, l) => t + l.otb, 0);
  return { value, periods: later.map((l) => l.period) };
};

/** Whether a line shows a value in this period at all (OTB / On Order: the target only; Act: not in the future). */
export const lineShown = (line: MatrixLine, period: number, ctx: CellContext, o: PlanOtbOptions): boolean => {
  if (isTotalOnly(line.metric)) return false;
  if (SPAN_METRICS.includes(line.metric)) return period === ctx.targetPeriod;
  if (line.type === 'plan' || line.type === 'ly') return true; // LY exists for future periods too
  // the current period's actuals always show; Hide future periods hides only what the periods after it would carry
  // (the LY in place, `lineCells`), so `o.hideFutureActuals` does not blank the current period any more.
  return period <= (ctx.currentPeriod ?? ctx.targetPeriod);
};

/**
 * Act tone against plan, or null (no plan, monthly-only plan, Plan line). Inflows use the inventory band (±10% green,
 * ±25% amber, red beyond: an inflow far under plan is a miss too); Markdowns are green at or below plan, amber up to +25% over, red beyond.
 */
export const actTone = (metric: PlanOtbMetric, act: number | null, plan: number | null): Tone | null => {
  if (!isNum(act) || !isNum(plan) || !isPositive(plan)) return null;
  if (metric === 'sales') return act >= plan ? 'good' : 'bad';
  if (metric !== 'inventory' && metric !== 'markdowns' && metric !== 'inflow') return null;
  const off = metric === 'markdowns' ? Math.max(0, act / plan - 1) : Math.abs(act / plan - 1);
  if (off <= 0.1 + TOLERANCE) return 'good';
  return off <= 0.25 + TOLERANCE ? 'warn' : 'bad';
};

/**
 * the OTB + Dev figure spelled out from the row's OTB and deviation (plan BOM − actual BOM at the start of the span):
 * "OTB $448 − $16,437 over plan BOM" (the deviation is negative: actual BOM over plan), "OTB $448 + $500 under plan BOM"; null when either is missing.
 */
export const otbDevText = (otb: number | null | undefined, deviation: number | null | undefined, grain?: Grain | null): string | null => {
  if (!isNum(otb) || !isNum(deviation)) return null;
  const bom = periodLabels(grain ?? 'monthly').bom;
  if (deviation === 0) return `OTB ${fmtCurrency(otb)}, actual ${bom} on plan`;
  return `OTB ${fmtCurrency(otb)} ${deviation > 0 ? '+' : '−'} ${fmtCurrency(Math.abs(deviation))} ${deviation > 0 ? 'under' : 'over'} plan ${bom}`;
};

const NO_FLAGS: CellFlags = { estimated: false, allocated: false, projected: false, lyPartial: false };
// `isEstimated` means the period's inventory was back-calculated (the kit's `flagsOf` rule in
// metricDefinitions.ts): only the Inventory Act line carries the estimated marker; Sales / Inflow /
// Markdowns Act are measured and every Plan line is a plan.
// PROJECTED only on the Sales Act of a trended cell.
const flagsOf = (c: Cell | undefined, inventoryDerived: boolean, salesTrended = false): CellFlags =>
  c ? { estimated: inventoryDerived && c.isEstimated, allocated: c.isAllocated, projected: salesTrended && c.isProjected && isTrendCell(c), lyPartial: false } : NO_FLAGS;

const PLAN_IDS: Record<PlanOtbMetric, string> = { sales: 'salesPlan', inventory: 'inventoryPlan', inflow: 'inflowPlan', markdowns: 'markdownsPlan', onOrder: 'planOtb:onOrderPlan', otb: 'otb', otbDev: 'otbDev', otbPeriod: 'planOtb:otbPeriod', otbRunning: 'planOtb:otbRunning' };
const makeDefinition = (metric: PlanOtbMetric, type: LineType): MetricDefinition => ({
  id: type === 'plan' ? PLAN_IDS[metric] : type === 'ly' ? `planOtb:${metric}:ly` : `planOtb:${metric}`,
  label: lineName({ metric, type }),
  shortLabel: typeLabelOf({ metric, type }) || METRIC_LABELS[metric],
  description: '',
  kind: 'currency',
  align: 'right',
  defaultWidth: 110,
  tiers: ['A', 'B', 'C'],
  reports: ['plan_otb'],
  defaultVisible: true,
  group: 'otb',
  cell: (row, ctx) => {
    // W4: the LY line reads last year's figure for the period; no cost figure at cost; "partial history" where LY starts before the client's data.
    if (type === 'ly') {
      const c = row.periods[String(ctx.targetPeriod)];
      const ly = lyValue(metric, c, ctx.basis);
      if (!ly.available) return notAtCostCell();
      const lyMissing = metric === 'inventory' && c?.inventoryLyMissing === true;
      return { value: ly.value, text: fmtCurrency(ly.value), flags: { ...NO_FLAGS, lyPartial: !lyMissing && c?.lyPartial === true }, ...(lyMissing ? { title: 'No last-year BOM: the client has no inventory history for the month before it' } : {}) };
    }
    // D4: a class without a weekly plan reads "No weekly plan" on its Plan lines (OTB and OTB + Dev included).
    if (type === 'plan' && metric !== 'onOrder' && hasNoWeeklyPlan(row)) return noWeeklyPlanCell();
    // no plan loaded for the period: its Plan lines (OTB and OTB + Dev included) read "—", never −on order.
    if (type === 'plan' && metric !== 'onOrder' && hasNoPlanLoaded(row)) return noPlanLoadedCell();
    if (isPeriodOtb(metric)) {
      if (ctx.basis === 'cost') return notAtCostCell();
      const line = periodOtbLine(row, ctx.targetPeriod, ctx);
      const v = periodOtbValue(row, metric, line);
      // Weekly: the on-order dates behind the figure are the POS's expected dates (often the cancel date), so each week's cell says so.
      const caveat = ctx.grain === 'weekly' ? ` ${ON_ORDER_WEEKLY_CELL_CAVEAT}` : '';
      return { value: v, text: fmtCurrency(v), flags: NO_FLAGS, ...(line ? { title: `${metric === 'otbRunning' ? runningTitle(line, fmtCurrency, ctx.grain) : `OTB for ${periodFullName(line.period, ctx.grain)}: planned inflows ${fmtCurrency(line.inflowPlan)} − received ${fmtCurrency(line.received)} − on order due ${fmtCurrency(line.onOrderDue)}. ${OTB_PERIOD_RULE_NOTE}`}${caveat}` } : {}) };
    }
    const c = row.periods[String(ctx.targetPeriod)];
    const mv = metricValues(metric, c, row, ctx.basis, ctx.trendMethod, { period: ctx.targetPeriod, grain: ctx.grain, salesThrough: ctx.salesThrough, current: ctx.currentPeriod ?? ctx.targetPeriod });
    const v = mv[type as 'plan' | 'act'];
    const flags = SPAN_METRICS.includes(metric) ? NO_FLAGS : flagsOf(c, metric === 'inventory' && type === 'act', metric === 'sales' && type === 'act');
    // no cost figure for this value: "—" titled "not available at cost", never the retail figure.
    if (!(type === 'plan' ? mv.planAvailable : mv.actAvailable)) return notAtCostCell(flags);
    // Row-level span figures carry no period flags; a partial-coverage OTB is titled "OTB through <week>" (D4).
    const title = (metric === 'otb' || metric === 'otbDev') && isNum(v) ? otbThroughTitle(row, ctx) : undefined;
    return { value: v, text: fmtCurrency(v), flags, ...(title ? { title } : {}) };
  },
});
const DEFINITIONS = new Map<string, MetricDefinition>(METRIC_ORDER.flatMap((m) => (['plan', 'act', 'ly'] as LineType[]).map((t) => [`${m}:${t}`, makeDefinition(m, t)] as [string, MetricDefinition])));

/** The (module-level, stable) column definition of a line; its `cell` reads `ctx.targetPeriod`. */
export const lineDefinition = (line: MatrixLine): MetricDefinition => DEFINITIONS.get(`${line.metric}:${line.type}`) as MetricDefinition;

export const periodCtx = (ctx: CellContext, period: number): CellContext => ({ ...ctx, targetPeriod: period });

/**
 * `trend`: the Sales Act's trend values in a trend period (sub-line + title); null on every other cell. `available` false: no
 * cost figure on the cost basis, the cell reads "—" and the line's Total too.
 */
export interface LineCell {
  period: number; shown: boolean; value: number | null; tone: Tone | null; trend: TrendValues | null; available: boolean;
  /** Sales Act in a trend period: the Sales Trend on the displayed basis (the second line; `value` is the actual to date). */
  trended: number | null;
  /** Inflow / Markdowns Act in a trend period, prorated: "WTD" / "MTD" with the days behind it; null otherwise. */
  proration: ProratedPlan | null;
  /** W4, Act cells with Show LY on: the variance against last year (null elsewhere, and where either figure is missing). */
  lyVariance: LyVariance | null;
  /** Show LY on an Act cell (retail only): 'inPlace' where the actual is not shown (future / hidden), 'under' in the current period; null elsewhere. */
  ly: LyInAct | null;
}

/** Last year's figure carried by an Act cell, and where it sits. `partial`: the LY starts before the client's data. */
export interface LyInAct { value: number; placement: 'inPlace' | 'under'; partial: boolean }
/** "LY $1,234" (the in-place cell and the current period's sub-line). */
export const lyText = (value: number): string => `LY ${fmtCurrency(value)}`;
/** The title of an in-place LY cell. */
export const lyInPlaceTitle = (ly: LyInAct): string => `Last year: ${fmtCurrency(ly.value)}${ly.partial ? ' (partial history: last year starts before your data)' : ''}`;

/** Act minus LY and as a percent of LY (null when LY is not positive). */
export interface LyVariance { diff: number; pct: number | null }
/** The Act cell's tooltip line: "vs LY +12.3% (+$1,200)". */
export const lyVarianceTitle = (v: LyVariance): string => `vs LY ${v.pct === null ? '' : `${v.pct >= 0 ? '+' : '−'}${Math.abs(v.pct).toFixed(1)}% `}(${fmtDiffCurrency(v.diff)})`;

/** The trend-period Sales Act cell's second line ("Trended $1,200"); the elapsed share is in its title (`trendCellTitle`). */
export const trendedText = (trended: number): string => `Trended ${fmtCurrency(trended)}`;
/** The trend-period Sales Act cell's sub-line ("50% elapsed"), kept for the title and tests. */
export const trendElapsedText = (t: TrendValues): string => `${fmtPct(t.trendPct, 0)} elapsed`;
/** The title of an Inflow or Markdowns Act cell compared with the prorated plan (its sub-line reads "WTD" / "MTD"). */
export const prorationCellTitle = (p: ProratedPlan): string | undefined => prorationTitle(p);
/** Its title: the elapsed share, then the basis as the kit words it ("(requested: …)" when a chosen method fell down the chain; the planShapeZero note through salesThrough). */
export const trendCellTitle = (t: TrendValues, method: TrendMethod = 'auto', salesThrough?: string | null, lag?: string | null): string =>
  `Trended: ${fmtPct(t.trendPct, 0)} of the period elapsed · ${trendTitle(t, method, salesThrough, lag)}`;
/** "sales through Oct 14 (3 days to come)" for an ended period whose sales lag, or null. */
export const periodLagText = (period: number, ctx: CellContext): string | null => salesLagText(period, ctx.grain, ctx.salesThrough, ctx.currentPeriod ?? ctx.targetPeriod);

/** A period column's CSV header: its label, marked "(trend)" when the period is trended (its Sales Act is the Sales Trend). */
export const csvPeriodHeader = (period: number, ctx: CellContext, trendPeriods: number[]): string =>
  `${formatPeriodLabel(period, ctx.grain)}${trendPeriods.includes(period) ? ' (trend)' : ''}`;

/** Every period cell of one line plus its Total (see the header for the Total rules). */
export interface LineCells { cells: LineCell[]; total: number | null; includesTrend: boolean; /** How many periods the Total adds (null for the span-level lines: OTB, On Order, ...). */ totalPeriods: number | null }
export const lineCells = (row: AnyRow, line: MatrixLine, periods: number[], ctx: CellContext, o: PlanOtbOptions): LineCells => {
  const cells = periods.map((period): LineCell => {
    if (isPeriodOtb(line.metric)) {
      const l = periodOtbLine(row, period, ctx);
      return { period, shown: l !== null, value: ctx.basis === 'cost' ? null : periodOtbValue(row, line.metric, l), available: l === null || ctx.basis !== 'cost', tone: null, trend: null, trended: null, proration: null, lyVariance: null, ly: null };
    }
    const shown = lineShown(line, period, ctx, o);
    const c = row.periods[String(period)];
    const mv = metricValues(line.metric, c, row, ctx.basis, ctx.trendMethod, { period, grain: ctx.grain, salesThrough: ctx.salesThrough, current: ctx.currentPeriod ?? ctx.targetPeriod });
    const lyHere = line.type === 'ly' ? lyValue(line.metric, c, ctx.basis) : null;
    const value = shown ? (lyHere ? lyHere.value : mv[line.type as 'plan' | 'act']) : null;
    const available = !shown || (lyHere ? lyHere.available : line.type === 'plan' ? mv.planAvailable : mv.actAvailable);
    const colourable = shown && available && line.type === 'act' && !o.hideRowColours;
    // W4: with Show LY on, an Act cell carries its variance against LY (retail), and is coloured by it where no plan colours it.
    const lyRetail = lyValue(line.metric, c, 'retail').value;
    const lyApplies = line.type === 'act' && lyInAct(o, line.metric, ctx.grain);
    const lyOn = lyApplies && shown && isNum(mv.actRetail) && isNum(lyRetail);
    const current = ctx.currentPeriod ?? ctx.targetPeriod;
    // Hide future periods keeps the LY out of the periods after the current one (nothing in place there).
    const lyPlacement = !lyApplies || ctx.basis === 'cost' || !isNum(lyRetail) ? null : !shown ? (o.hideFutureActuals ? null : 'inPlace') : period === current ? 'under' : null;
    const ly: LyInAct | null = lyPlacement ? { value: lyRetail as number, placement: lyPlacement, partial: c?.lyPartial === true } : null;
    const lyVariance: LyVariance | null = lyOn ? { diff: (mv.actRetail as number) - (lyRetail as number), pct: isUsablePctBase(lyRetail as number) ? ((mv.actRetail as number) / (lyRetail as number) - 1) * 100 : null } : null;
    // The tone compares the retail actual with the retail plan, whatever basis the cell shows.
    const planTone = colourable ? actTone(line.metric, mv.actRetail, mv.planRetail) : null;
    return { period, shown, value, available, lyVariance, ly, tone: planTone ?? (colourable && lyVariance ? actTone(line.metric, mv.actRetail, lyRetail) : null),
      // The sub-line only under a trended value: a later-started trend period with no elapsed sales has Trend % 0 and no Sales Trend.
      trend: shown && line.type === 'act' && isNum(mv.trended) ? mv.trend : null,
      trended: shown && line.type === 'act' && isNum(mv.trended) ? mv.trended : null,
      proration: shown && line.type === 'act' && mv.proration?.label && isNum(mv.act) ? mv.proration : null };
  });
  if (isPeriodOtb(line.metric)) {
    // The whole span's figure, whichever periods the columns reach; none at cost or without lines.
    const t = ctx.basis === 'cost' ? null : periodOtbTotal(row, line.metric);
    return { cells, total: t, includesTrend: false, totalPeriods: null };
  }
  if (isTotalOnly(line.metric)) {
    // The span's figure, in the Total only (the definition owns the cost / no-plan rules; "—" when it has no number).
    const v = lineDefinition(line).cell(row, periodCtx(ctx, ctx.targetPeriod)).value;
    return { cells, total: typeof v === 'number' && Number.isFinite(v) ? v : null, includesTrend: false, totalPeriods: null };
  }
  // an in-progress period adds its Sales Trend (not its actuals so far) to the Total, labelled "incl. trend".
  // the LY Total adds the periods the Plan Total does (those with a plan figure), so the two compare like for like (the LY
  // cells exist for every window period, the plan only through the weeks it was loaded for: 10 weeks of LY against 7 of plan). A row with
  // no plan figure in any period keeps the whole window.
  const plannedCells = line.type === 'ly' ? cells.filter((c) => c.shown && isNum(metricValues(line.metric, row.periods[String(c.period)], row, ctx.basis, ctx.trendMethod, { period: c.period, grain: ctx.grain, salesThrough: ctx.salesThrough, current: ctx.currentPeriod ?? ctx.targetPeriod }).planRetail)) : [];
  const counted = plannedCells.length > 0 ? plannedCells : cells;
  const nums = counted.map((c) => (isNum(c.trended) ? c.trended : c.value)).filter(isNum);
  const includesTrend = cells.some((c) => isNum(c.trended));
  let total: number | null = null;
  // BOM inventory's Total is the CURRENT period's value on every line (Plan, Act and LY alike), so Plan and Act are at the
  // same period and compare; "—" when that period is not a shown column or the line shows nothing there. A stock figure is not additive.
  if (line.metric === 'inventory') {
    const at = cells.find((c) => c.period === (ctx.currentPeriod ?? ctx.targetPeriod));
    const v = at && at.shown && at.available && isNum(at.value) ? at.value : null;
    return { cells, total: v, includesTrend: false, totalPeriods: v !== null ? 1 : null };
  }
  // At cost, a line with any shown cell lacking its cost has no Total (no partial cost totals).
  if (nums.length > 0 && cells.every((c) => c.available)) total = nums.reduce((a, b) => a + b, 0);
  return { cells, total, includesTrend: includesTrend && total !== null, totalPeriods: total !== null ? nums.length : null };
};

/**
 * A cell's number for the CSV: the same value the matrix renders through the line
 * definition, so one reading path; null (a blank CSV cell) when the cell is not shown (a future period, Hide future actuals), has no
 * figure at cost, or the definition says text ("No weekly plan", "—"). The Sales Act's value is the ACTUAL to date; the trended
 * sales are their own CSV line (`trendedLineNumber`).
 */
export const lineCellNumber = (row: AnyRow, line: MatrixLine, cell: LineCell, ctx: CellContext): number | null => {
  if (!cell.shown) return null;
  const v = lineDefinition(line).cell(row, periodCtx(ctx, cell.period)).value;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

/**
 * The "Sales Trended" CSV line's cell: a trend period's Sales Trend (blank when it has none), and for every other period the
 * actual, which is what that period trends to, so the line's cells add up to its Total (the matrix Total "incl. trend").
 */
export const trendedLineNumber = (row: AnyRow, line: MatrixLine, cell: LineCell, ctx: CellContext, trendPeriods: number[]): number | null =>
  trendPeriods.includes(cell.period) ? (isNum(cell.trended) ? cell.trended : null) : lineCellNumber(row, line, cell, ctx);

/** The sum of the shown figures of a CSV line (blank without any, and at cost when a shown cell lacks its cost figure, as `lineCells`). */
export const sumLineNumbers = (nums: (number | null)[], cells: LineCell[]): number | null => {
  const present = nums.filter((n): n is number => n !== null);
  return present.length > 0 && cells.every((c) => c.available) ? present.reduce((a, b) => a + b, 0) : null;
};

// ---- Benchmark line -------------------------------------------------------------------------------
// The matrix's columns are periods and its lines are dollars; a benchmark is a ratio for ONE month, so it is not a
// per-period line. Each class and location block gets a single "Benchmark" line behind Options › Show benchmarks (default
// off, level 'class' / 'location' only): class rows against their master class's G3 pool, location subtotals against the
// vertical's G4 (the entries the hook resolved into `ctx.benchmarks.byRowKey`, the same ones the benchmark columns read).
// Vendor, product and SKU rows have no entry, so never a line. Peer values go through the kit's gate (`gatedPeerValue` /
// `peerText`: nothing for an unpublishable grain; the three metrics here carry no inflow gate).
const PLAN_OTB_BENCH: { id: BenchmarkMetricId; label: string }[] = [
  { id: 'turnover', label: 'Turnover' },
  { id: 'stockToSales', label: 'Stock-to-sales' },
  { id: 'markdownPct', label: 'Markdown' },
];

/** The sentence for a block whose entry has no peer figures to show (not ok), or null when it does. */
const benchmarkStatusNote = (v: BenchmarkRowValue, month: string): string | null => {
  if (v.status === 'mixedMasterClasses') return `Child classes span several master classes: no single peer group (${month})`;
  if (v.status === 'noMasterClass') return `No master class mapped for this class (${month})`;
  if (v.status === 'noPeerGroup' || !v.peer) return `No peer group for ${month}`;
  if (v.status !== 'ok') return `Not enough peers${v.masterClass ? ` in ${v.masterClass}` : ''} for ${month}`;
  return null;
};

/** The block's benchmark entry, or undefined (no benchmarks, a vendor / product / SKU row, no entry). */
const entryOf = (flatRow: FlatRow, ctx: CellContext): BenchmarkRowValue | undefined => {
  const b = ctx.benchmarks;
  return b && !isVendorRow(flatRow.row) ? b.byRowKey[flatRow.row.key] : undefined;
};

/**
 * The block's one-line benchmark text, or null when the row has none (no benchmarks, a vendor / product / SKU row, no entry).
 * Says what the peer figures are: "Sep 2026 month-end" (the latest complete month when it is a fallback), and in Weekly that they are monthly.
 */
export const benchmarkLineText = (flatRow: FlatRow, ctx: CellContext): string | null => {
  const b = ctx.benchmarks;
  const v = entryOf(flatRow, ctx);
  if (!b || !v) return null;
  const note = benchmarkStatusNote(v, benchmarkMonthNote(b));
  if (note !== null) return note;
  const peer = v.peer as BenchmarkPeer;
  const parts = PLAN_OTB_BENCH.map(({ id, label }) => {
    const idx = benchmarkIndex(id, v.own[id], gatedPeerValue(peer, id));
    return `${retailTag(label, ctx.basis)} ${formatBenchmark(id, v.own[id])} · peers ${peerText(peer, id)} (index ${formatIndex(id, idx)})`;
  });
  return [...parts, benchmarkMonthEnd(b), ...(ctx.grain === 'weekly' ? ['peer figures are monthly'] : []), ...(v.filtered ? ['filtered'] : [])].join(' · '); // Adam, 2026-10-20: no peer counts on screen
};

/** One benchmark metric of a block as numbers for the CSV: percent metrics in percent points, the index a plain ratio. */
export interface BenchmarkCsvRow { label: string; unit: 'ratio' | '%' | 'weeks'; own: number | null; peer: number | null; index: number | null }
/** A block's benchmark as CSV rows: one per metric when there are peer figures, else only the sentence saying why not. */
export interface BenchmarkCsvBlock { rows: BenchmarkCsvRow[]; note: string | null; basis: string }

const UNIT_OF = { ratio: 'ratio', pct: '%', weeks: 'weeks' } as const;
export const benchmarkCsvBlock = (flatRow: FlatRow, ctx: CellContext, options: PlanOtbOptions): BenchmarkCsvBlock | null => {
  const b = ctx.benchmarks;
  const v = options.showBenchmarks ? entryOf(flatRow, ctx) : undefined;
  if (!b || !v) return null;
  const basis = benchmarkMonthEnd(b);
  const note = benchmarkStatusNote(v, benchmarkMonthNote(b));
  if (note !== null) return { rows: [], note, basis };
  const peer = v.peer as BenchmarkPeer;
  const scale = (id: BenchmarkMetricId, x: number | null): number | null => (x === null ? null : BENCHMARK_METRIC_BY_ID[id].format === 'pct' ? x * 100 : x);
  const rows = PLAN_OTB_BENCH.map(({ id, label }) => {
    const own = v.own[id];
    const peerValue = gatedPeerValue(peer, id);
    return { label: retailTag(label, ctx.basis), unit: UNIT_OF[BENCHMARK_METRIC_BY_ID[id].format], own: scale(id, isNum(own) ? own : null), peer: scale(id, peerValue), index: benchmarkIndex(id, own, peerValue) };
  });
  return { rows, note: null, basis: [basis, ...(ctx.grain === 'weekly' ? ['peer figures are monthly'] : []), ...(v.filtered ? ['filtered'] : [])].join(' · ') };
};

/** The line the matrix and the CSV draw for a block: only with Options › Show benchmarks on. */
export const benchmarkLineFor = (flatRow: FlatRow, ctx: CellContext, options: PlanOtbOptions): string | null =>
  options.showBenchmarks ? benchmarkLineText(flatRow, ctx) : null;

// The Plan & OTB period matrix (Plan 4 spec 7.2): one block of metric lines (Plan / Act)
// per FlatRow the row tree shows (so view, filters, search, sort, Show SKUs and expansion apply),
// one column per display period, then Total. Each <tbody> is one row block (`aria-label` = the row
// label, `data-row-id` = the FlatRow id the attention list scrolls to). Cells render through the
// kit's `renderCellContent` with a per-period ctx, so the estimated / allocated /
// projected markers match the other reports. Value, blanking, colour and Total rules live in
// planOtbCells.ts, which the CSV shares. In a trend period the Sales Act cell is the Sales
// Trend with a muted "<Z>% elapsed" sub-line whose title gives the basis (`trendCellTitle`); the CSV
// marks that period's header "(trend)". The first header is the view's `firstColumn` (its description
// as title); placeholders load through `model.loadChildRows` (products in product views, SKUs
// otherwise). Sticky header; the label, Metric and Type columns are sticky
// row headers (`<th scope="row">`, left offsets from their fixed widths) so Plan / Act stays readable
// across a wide period window; the label cell spans the block's lines; hover is React state, as in the kit table.

const LABEL_WIDTH = 240;
const METRIC_WIDTH = 110;
const TYPE_WIDTH = 64;
/** Sticky left offsets and fixed widths of the Metric and Type columns (header and body share them). */
const METRIC_STYLE: React.CSSProperties = { left: LABEL_WIDTH, width: METRIC_WIDTH, minWidth: METRIC_WIDTH, maxWidth: METRIC_WIDTH };
const TYPE_STYLE: React.CSSProperties = { left: LABEL_WIDTH + METRIC_WIDTH, width: TYPE_WIDTH, minWidth: TYPE_WIDTH, maxWidth: TYPE_WIDTH };
const TYPE_EDGE_CSS = 'border-r border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0]';
const CARD_CSS = 'bg-white border border-[#E4E4E7] rounded-2xl shadow-sm overflow-hidden relative';
const CURRENT_CSS = 'bg-[#EFF8FF]';
const TOTAL_CSS = 'bg-[#F4F5F7]';
const HEAD_CELL_CSS = 'px-4 py-2.5 sticky top-0 z-20 bg-[#F9F9FB] whitespace-nowrap';
const CELL_CSS = 'px-4 py-2 whitespace-nowrap text-right tabular-nums text-[13px] leading-[19.5px]';
const GROUP_KINDS: FlatRow['kind'][] = ['group', 'subtotal'];
const THIS_TAG_CSS = 'px-1.5 py-0.5 rounded-[5px] bg-[#F9F5FF] text-[#6941C6] border border-[#E9D7FE] text-[10px] font-bold';


interface BlockProps {
  flatRow: FlatRow;
  /** a child class unrolled under its parent class: the parent's label (the block is called out); undefined otherwise. */
  childOf?: string;
  /** what-if Plan Sales edits (absent = not editable, e.g. at cost). */
  planEdit?: PlanEditProps;
  /** QA fix 4: the "Later" column is on (some row has span months past the shown columns). */
  withLater: boolean;
  lines: MatrixLine[];
  periods: number[];
  ctx: CellContext;
  options: PlanOtbOptions;
  currentPeriod: number;
  onToggleGroup: (key: string) => void;
}

/** What a block needs to edit its Plan Sales cells: the original plan of each edited cell, and the setter (null clears the edit). */
interface PlanEditProps { originals: PlanEdits; raised: PlanEdits; changed: PlanEditChanges; onEdit: (rowKey: string, period: number, value: number | null) => void }

/** The tooltip of a plan BOM the what-if moved (no negative inflows; signed amount). buyer wording, no Plan Builder reference. */
export const raisedBomTitle = (amount: number): string => amount < 0
  ? `Lowered ${fmtCurrency(-amount)} by your plan sales edit: the plan would have needed a negative buy in a later period, so that amount came off this period's buy instead.`
  : `Raised ${fmtCurrency(amount)} by your plan sales edit: a negative buy could not be taken off an earlier period, so the stock stays on hand and this period's buy is lower.`;

/** 8a (Wendi, 2026-10-22): the tooltip of any other cell a plan sales edit moved (the moved BOMs keep `raisedBomTitle`). */
export const changedByEditTitle = (was: number): string => `Changed by your plan sales edit (what-if): the plan is ${fmtCurrency(was)}.`;
/** The `changed` field a matrix line reads (null for lines an edit never moves). */
const changedFieldOf = (line: MatrixLine): string | null =>
  line.type === 'plan' && (line.metric === 'sales' || line.metric === 'markdowns' || line.metric === 'inventory' || line.metric === 'inflow') ? `${line.metric === 'inventory' ? 'inventory' : line.metric}Plan`
    : line.metric === 'otbPeriod' || line.metric === 'otbRunning' ? line.metric : null;
const ChangedMark: React.FC<{ was: number; children: React.ReactNode }> = ({ was, children }) => (
  <span className="inline-flex items-center gap-1" title={changedByEditTitle(was)}>
    <PencilLine className="w-3 h-3 shrink-0 text-[#7F56D9]" aria-label="Changed by your edit" />
    {children}
  </span>
);

const parseAmount = (text: string): number | null => {
  const n = Number(text.replace(/[$,\s]/g, ''));
  return text.trim() === '' || !Number.isFinite(n) || n < 0 ? null : n;
};

/**
 * a Plan Sales cell the buyer can click and type a new figure into (a what-if, kept in the view). Enter or leaving the
 * field applies it, Escape cancels, an empty field (or the original figure) clears the edit. An edited cell shows a pencil by the new
 * figure and the plan's own figure small underneath ("Plan $X").
 */
export const EditablePlanSales: React.FC<{ value: number | null; original: number | undefined; label: string; onCommit: (value: number | null) => void }> = ({ value, original, label, onCommit }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const edited = original !== undefined;
  const start = () => {
    setText(value === null ? '' : String(Math.round(value)));
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    const n = parseAmount(text);
    if (text.trim() === '' || (edited && n !== null && Math.round(n) === Math.round(original as number))) onCommit(null);
    else if (n !== null && (value === null || Math.round(n) !== Math.round(value))) onCommit(n);
  };
  if (editing) {
    return (
      <input
        aria-label={`Plan sales for ${label}`}
        autoFocus
        inputMode="decimal"
        value={text}
        onFocus={(e) => e.target.select()} // typing replaces the figure
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="w-[96px] h-7 px-2 rounded-[6px] border border-[#7F56D9] text-right text-[13px] tabular-nums focus:outline-none focus:ring-2 focus:ring-[#7F56D9]/30"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={start}
      aria-label={`Edit plan sales for ${label}`}
      title={edited ? `Edited plan sales (what-if). The plan is ${fmtCurrency(original as number)}. Click to change; clear the field to go back to the plan.` : 'Click to try a different plan sales figure (a what-if; the plan itself does not change)'}
      className="inline-flex flex-col items-end leading-tight rounded-[4px] px-1 -mx-1 hover:bg-[#F4EBFF] cursor-text"
    >
      <span className={cn('inline-flex items-center gap-1', edited && 'text-[#6941C6] font-semibold')}>
        {edited && <PencilLine className="w-3 h-3 shrink-0" aria-label="Edited" />}
        {value === null ? EMPTY : fmtCurrency(value)}
      </span>
      {edited && <span className="text-[11px] font-medium text-[#98A2B3]">Plan {fmtCurrency(original as number)}</span>}
    </button>
  );
};

// the call-out on a child class unrolled under its parent: a violet left edge on the label and a "Child of <parent>" tag.
const CHILD_EDGE_CSS = 'border-l-[3px] border-l-[#A78BFA]';
const CHILD_TAG_CSS = 'inline-flex items-center gap-1 max-w-full px-1.5 py-0.5 rounded-[5px] bg-[#EDE9FE] text-[11px] font-semibold text-[#6D28D9] leading-[14px] cursor-help';

/** The label of each child class unrolled under a parent class, by row id: the nearest row above it one level up, when that row is a class. */
export const childClassParents = (flatRows: readonly FlatRow[]): Map<string, string> => {
  const out = new Map<string, string>();
  flatRows.forEach((fr, i) => {
    const r = fr.row as { parentLocationClassId?: number | null; labelAs?: string; summary?: { planParentLabel?: string | null } | null };
    // A location / vendor row labelled as such (Class › Location) is a store's copy, not a child class.
    if (fr.depth === 0 || fr.kind === 'skuPlaceholder' || r.parentLocationClassId == null || r.labelAs) return;
    for (let j = i - 1; j >= 0; j -= 1) {
      if (flatRows[j].depth < fr.depth) {
        out.set(fr.id, r.summary?.planParentLabel ?? rowLabelText(flatRows[j]));
        return;
      }
    }
  });
  return out;
};

const LabelCell: React.FC<{ flatRow: FlatRow; rowSpan: number; bg: string; onToggleGroup: (key: string) => void; childOf?: string; note?: string; noteTitle?: string; children?: React.ReactNode }> = ({ flatRow, rowSpan, bg, onToggleGroup, childOf, note, noteTitle, children }) => {
  const group = GROUP_KINDS.includes(flatRow.kind);
  const name = rowLabelText(flatRow);
  const expandable = flatRow.childCount > 0 && flatRow.kind !== 'skuPlaceholder';
  const Chevron = flatRow.expanded ? ChevronDown : ChevronRight;
  const label = children ?? <DefaultRowLabel flatRow={flatRow} />;
  return (
    <th
      scope="row"
      data-label-cell
      rowSpan={rowSpan}
      className={cn(STICKY_LABEL_CELL_CSS, bg, 'py-2 align-top text-left', group ? 'text-[14px] font-bold text-[#101828]' : 'text-[13px] font-semibold text-[#101828]', childOf && CHILD_EDGE_CSS)}
      style={{ width: LABEL_WIDTH, minWidth: LABEL_WIDTH, maxWidth: LABEL_WIDTH, paddingLeft: 16 + flatRow.depth * 20 }}
    >
      {expandable ? (
        <button
          type="button"
          aria-expanded={flatRow.expanded}
          aria-label={`${flatRow.expanded ? 'Collapse' : 'Expand'} ${name}`}
          onClick={() => onToggleGroup(flatRow.expandKey ?? flatRow.row.key)}
          className="inline-flex items-center gap-1.5 max-w-full text-left cursor-pointer select-none"
        >
          <Chevron className="w-4 h-4 text-[#475467] shrink-0" aria-hidden="true" />
          {label}
        </button>
      ) : (
        <span className="inline-flex items-center gap-1.5 max-w-full pl-[22px]">{label}</span>
      )}
      {childOf && (
        <div className="pl-[22px] mt-1">
          <span className={CHILD_TAG_CSS} title={`A child class unrolled from ${childOf}: its actuals only. The parent's plan and OTB cover it, and the parent's figures already include it.`}>
            <CornerDownRight className="w-3 h-3 shrink-0" aria-hidden="true" />
            <span className="truncate">Child of {childOf}</span>
          </span>
        </div>
      )}
      {note && <div className="pl-[22px] text-[11px] font-medium text-[#98A2B3]" title={noteTitle}>{note}</div>}
    </th>
  );
};

/** the Running OTB line's Total is the span's last running figure, not a sum. */
export const RUNNING_OTB_TOTAL_TITLE = "The span's final running OTB (the OTB Total)";

/**
 * what a Total adds. The LY Total adds the periods that have a plan, so it is like-for-like with the Plan Total.
 * BOM inventory's Total is the current period on every line (Plan and Act at the same period); an Act Total that adds
 * fewer periods than its Plan Total (`planN`) says so ("Total of 4 of 6 months shown (actuals so far)"); the Running OTB Total is the
 * span's final figure; the OTB + Dev Total spells out OTB and the deviation.: the OTB by period
 * Total is the span's OTB (there is no OTB line of its own any more), and says so.
 */
export const totalTitle = (line: MatrixLine, n: number | null, grain: Grain, { currentPeriod, planN, otbDev }: { currentPeriod: number; planN?: number | null; otbDev?: string | null }): string | undefined => {
  if (line.metric === 'inventory') return `${bomInventoryLabel(grain)} at ${periodFullName(currentPeriod, grain)} (Plan and Act at the same period)`;
  if (line.metric === 'otbPeriod') return OTB_PERIOD_TOTAL_TITLE;
  if (line.metric === 'otbRunning') return RUNNING_OTB_TOTAL_TITLE;
  if (line.metric === 'otbDev') return otbDev ?? undefined;
  if (n === null) return undefined;
  const noun = (k: number) => `${periodNoun(grain)}${k === 1 ? '' : 's'}`;
  if (line.type === 'ly') return `Total of last year over the ${n} ${noun(n)} that have a plan, so it compares with the Plan Total`;
  if (line.type === 'act' && line.metric !== 'onOrder' && typeof planN === 'number' && n < planN) return `Total of ${n} of ${planN} ${noun(planN)} shown (actuals so far)`;
  return `Total of ${n} ${noun(n)}`;
};

const MatrixBlock: React.FC<BlockProps> = ({ flatRow, childOf, planEdit, withLater, lines: allLines, periods, ctx, options, currentPeriod, onToggleGroup }) => {
  const [hovered, setHovered] = useState(false);
  //: a child class unrolled from its parent shows its actuals only: the parent carries the plan and OTB, so the
  // child's Plan lines and OTB lines are left out (On Order stays: it is an actual).
  const lines = childOf ? allLines.filter((l) => l.type === 'act') : allLines;
  const group = GROUP_KINDS.includes(flatRow.kind);
  // A child class unrolled from its parent reads on a faint violet ground, so it never passes for a class of its own.
  const bg = hovered ? (group ? 'bg-[#F1F3F5]' : childOf ? 'bg-[#F4F0FF]' : 'bg-[#FAFAFC]') : group ? 'bg-[#F8F9FA]' : childOf ? 'bg-[#FAF8FF]' : 'bg-white';
  const text = group ? 'font-bold text-[#101828]' : 'font-semibold text-[#344054]';
  // QA fix 10: "No weekly plan" once per row (under its label); its Plan and OTB lines are blank.
  const noWeekly = hasNoWeeklyPlan(flatRow.row);
  // one extra "Benchmark" line (Options › Show benchmarks); the label cell spans it.
  const benchLine = benchmarkLineFor(flatRow, ctx, options);
  const rowCount = lines.length + (benchLine ? 1 : 0);
  return (
    <tbody
      aria-label={rowLabelText(flatRow)}
      data-row-id={flatRow.id}
      data-row-kind={flatRow.kind}
      className="border-b border-[#E4E4E7]"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {lines.map((line, i) => {
        const { cells, total, includesTrend, totalPeriods } = lineCells(flatRow.row, line, periods, ctx, options);
        const firstOfMetric = i === 0 || lines[i - 1].metric !== line.metric;
        const blank = noWeekly && line.type === 'plan' && line.metric !== 'onOrder';
        const later = withLater && isPeriodOtb(line.metric) ? laterOtb(flatRow.row, line.metric, periods, ctx) : null;
        // an Act Total names how many of the Plan Total's periods it adds; OTB + Dev's Total spells out OTB and the deviation.
        const planN = line.type === 'act' && line.metric !== 'onOrder' ? lineCells(flatRow.row, { metric: line.metric, type: 'plan' }, periods, ctx, options).totalPeriods : null;
        const title = total === null || blank || includesTrend ? undefined
          : totalTitle(line, totalPeriods, ctx.grain, { currentPeriod, planN, otbDev: line.metric === 'otbDev' ? otbDevText(flatRow.row.summary?.otb, flatRow.row.summary?.deviation, ctx.grain) : null });
        return (
          <tr key={`${line.metric}:${line.type}`} aria-label={lineName(line, ctx.grain)} className={cn('transition-colors', bg, (i < lines.length - 1 || benchLine !== null) && 'border-b border-[#F2F4F7]')}>
            {i === 0 && <LabelCell flatRow={flatRow} rowSpan={rowCount} bg={bg} onToggleGroup={onToggleGroup} childOf={childOf} note={noWeekly && !childOf ? NO_WEEKLY_PLAN_LABEL : undefined} noteTitle={noWeekly && !childOf ? NO_WEEKLY_PLAN_TOOLTIP : undefined} />}
            <th scope="row" style={METRIC_STYLE} className={cn('sticky z-10 px-4 py-2 whitespace-nowrap text-left text-[13px]', bg, text)}>
              {firstOfMetric && (
                <span className="inline-flex items-center gap-1">
                  {metricLabel(line.metric, ctx.grain)}
                  {otbLineTooltip(line.metric, ctx.grain) && <InfoTooltip text={otbLineTooltip(line.metric, ctx.grain) as string} widthClass="w-64" positionClass="top-full left-0 mt-1.5 z-[110]" />}
                </span>
              )}
            </th>
            <th scope="row" style={TYPE_STYLE} className={cn('sticky z-10 px-4 py-2 whitespace-nowrap text-left text-[12px] font-medium text-[#667085]', bg, TYPE_EDGE_CSS)}>{typeLabelOf(line)}</th>
            {cells.map((c) => {
              const editKey = planEdit && !blank && !childOf && line.metric === 'sales' && line.type === 'plan' && c.period >= ctx.targetPeriod && c.shown && c.value !== null ? planEditKeyOf(flatRow.row.key) : null;
              const raisedBy = planEdit && !blank && line.metric === 'inventory' && line.type === 'plan' && c.shown ? planEdit.raised[flatRow.row.key]?.[String(c.period)] : undefined;
              // 8a: any other cell the edit moved (a markdown, an inflow, a BOM that merely scaled, a store's sales under a chain edit, OTB by period / running).
              const changedField = planEdit && !blank && c.shown && !raisedBy && !editKey ? changedFieldOf(line) : null;
              const changedWas = changedField ? planEdit?.changed[flatRow.row.key]?.[String(c.period)]?.[changedField] : undefined;
              const lag = line.metric === 'sales' && line.type === 'act' && c.shown && !c.trend ? periodLagText(c.period, ctx) : null;
              const content = blank ? null
                : raisedBy ? (
                  <span className="inline-flex items-center gap-1" title={raisedBomTitle(raisedBy)}>
                    {raisedBy < 0
                      ? <ArrowDownToLine className="w-3 h-3 shrink-0 text-[#7F56D9]" aria-label="Lowered by your edit" />
                      : <ArrowUpFromLine className="w-3 h-3 shrink-0 text-[#7F56D9]" aria-label="Raised by your edit" />}
                    {renderCellContent(lineDefinition(line), flatRow, periodCtx(ctx, c.period))}
                  </span>
                )
                : editKey && planEdit ? <EditablePlanSales value={c.value} original={planEdit.originals[editKey]?.[String(c.period)]} label={`${rowLabelText(flatRow)}, ${periodFullName(c.period, ctx.grain)}`} onCommit={(v) => planEdit.onEdit(editKey, c.period, v)} />
                // An ended period whose sales lag its end (trended in the current period only): the Sales Act names the days to come.
                : changedWas !== undefined ? <ChangedMark was={changedWas}>{renderCellContent(lineDefinition(line), flatRow, periodCtx(ctx, c.period))}</ChangedMark>
                : c.shown && lag ? <span title={`Actual so far: ${lag}`}>{renderCellContent(lineDefinition(line), flatRow, periodCtx(ctx, c.period))}</span>
                : c.shown ? renderCellContent(lineDefinition(line), flatRow, periodCtx(ctx, c.period))
                // Show LY: a period whose actual is not shown reads last year's figure in place, muted and marked LY.
                : c.ly?.placement === 'inPlace' ? <span className="italic text-[#98A2B3]" title={lyInPlaceTitle(c.ly)}>{lyText(c.ly.value)}</span>
                  : <span className="text-[#98A2B3]">{EMPTY}</span>;
              // Show LY, current period: the actual keeps its place, last year's figure goes small underneath.
              const lyUnder = !blank && c.ly?.placement === 'under' ? <span className="text-[11px] font-medium text-[#98A2B3]" title={c.ly.partial ? 'Partial history: last year starts before your data' : 'Last year'}>{lyText(c.ly.value)}</span>
                // a past Act cell with no other sub-line (no trend, proration or LY under) shows its change against LY small underneath.
                : !blank && ctx.basis !== 'cost' && c.lyVariance && c.lyVariance.pct !== null && !c.trend && !c.proration?.label ? <span className="text-[11px] font-medium text-[#98A2B3]">{signedPct(c.lyVariance.pct / 100)} LY</span>
                : null;
              return (
                <td key={c.period} title={c.lyVariance ? lyVarianceTitle(c.lyVariance) : undefined} className={cn(CELL_CSS, line.type === 'ly' ? 'font-medium text-[#667085]' : text, c.period === currentPeriod && CURRENT_CSS, c.tone && TONE_CSS[c.tone])}>
                  {c.trend && c.trended !== null ? (
                    <span className="inline-flex flex-col items-end leading-tight" title={trendCellTitle(c.trend, ctx.trendMethod, ctx.salesThrough, periodLagText(c.period, ctx))}>
                      {content}
                      <span className="text-[11px] font-medium text-[#98A2B3]">{trendedText(c.trended)}</span>
                      {lyUnder}
                    </span>
                  ) : c.proration?.label ? (
                    <span className="inline-flex flex-col items-end leading-tight" title={prorationCellTitle(c.proration)}>
                      {content}
                      <span className="text-[11px] font-medium text-[#98A2B3]">{c.proration.label}</span>
                      {lyUnder}
                    </span>
                  ) : lyUnder ? (
                    <span className="inline-flex flex-col items-end leading-tight">
                      {content}
                      {lyUnder}
                    </span>
                  ) : content}
                </td>
              );
            })}
            {withLater && (
              <td className={cn(CELL_CSS, text)} title={later ? `Span ${periodNoun(ctx.grain)}s after the last column: ${later.periods.map((p) => periodFullName(p, ctx.grain)).join(', ')}` : undefined}>
                {isPeriodOtb(line.metric) ? (later ? fmtCurrency(later.value) : <span className="text-[#98A2B3]">{EMPTY}</span>) : <span className="text-[#98A2B3]">{EMPTY}</span>}
              </td>
            )}
            <td className={cn(CELL_CSS, 'font-bold text-[#101828]', TOTAL_CSS)} title={title}>
              {/* 8a: the span figures (OTB + Dev; the OTB by period / Running OTB Totals are the span's OTB) an edit moved. */}
              {blank ? null : total === null ? <span className="text-[#98A2B3]">{EMPTY}</span> : planEdit && (line.metric === 'otbDev' || isPeriodOtb(line.metric)) && planEdit.changed[flatRow.row.key]?.[PLAN_EDIT_SPAN_KEY]?.[line.metric === 'otbDev' ? 'otbDev' : 'otb'] !== undefined ? (
                <ChangedMark was={planEdit.changed[flatRow.row.key][PLAN_EDIT_SPAN_KEY][line.metric === 'otbDev' ? 'otbDev' : 'otb']}>{fmtCurrency(total)}</ChangedMark>
              ) : includesTrend ? (
                <span className="flex flex-col items-end leading-tight" title="The Total adds the trended sales (not the sales so far) of the period in progress">
                  {fmtCurrency(total)}
                  <span className={CELL_CAPTION_TEXT_CSS}>incl. trend</span>
                </span>
              ) : fmtCurrency(total)}
            </td>
          </tr>
        );
      })}
      {benchLine !== null && (
        <tr aria-label="Benchmark" className={cn('transition-colors', bg)}>
          <th scope="row" style={METRIC_STYLE} className={cn('sticky z-10 px-4 py-2 whitespace-nowrap text-left text-[13px]', bg, text)}>Benchmark</th>
          <th scope="row" style={TYPE_STYLE} className={cn('sticky z-10 px-4 py-2 whitespace-nowrap text-left text-[12px] font-medium text-[#667085]', bg, TYPE_EDGE_CSS)}>Peers</th>
          <td colSpan={periods.length + 1 + (withLater ? 1 : 0)} className="px-4 py-2 text-left text-[12px] font-medium text-[#475467] whitespace-nowrap">{benchLine}</td>
        </tr>
      )}
    </tbody>
  );
};

/** "Loading SKUs…" / "Loading products…" under its parent; asks `loadChildRows` once per mount (as the kit's ReportTableRow does). */
const SkuPlaceholderBlock: React.FC<{ flatRow: FlatRow; colSpan: number; onExpandSkus: (parentKey: string) => void }> = ({ flatRow, colSpan, onExpandSkus }) => {
  const requested = useRef(false);
  useEffect(() => {
    // SKU and product placeholders alike: `loadChildRows` routes product views to /product-rows.
    if (placeholderLoads(flatRow) && flatRow.parentKey && !requested.current) {
      requested.current = true;
      onExpandSkus(flatRow.parentKey);
    }
  }, [flatRow.parentKey, onExpandSkus]); // eslint-disable-line react-hooks/exhaustive-deps -- placeholderLoads(flatRow) is read once per mount (the ref), keyed by the parent
  return (
    <tbody aria-label={flatRow.row.key.endsWith(':products') ? 'Loading products' : 'Loading SKUs'} data-row-id={flatRow.id} data-row-kind="skuPlaceholder">
      <tr className="bg-white border-b border-[#EAECF0]">
        <LabelCell flatRow={flatRow} rowSpan={1} bg="bg-white" onToggleGroup={() => undefined} />
        <td colSpan={colSpan} />
      </tr>
    </tbody>
  );
};

const PeriodHeaderCell: React.FC<{ period: number; ctx: CellContext; current: boolean; hideDates: boolean }> = ({ period, ctx, current, hideDates }) => {
  const { main, sub } = periodHeader(period, ctx.grain);
  return (
    <th scope="col" className={cn(HEAD_CELL_CSS, 'text-right', current && CURRENT_CSS)} title={formatPeriodLabel(period, ctx.grain)}>
      <div className="flex flex-col items-end gap-0.5 leading-tight">
        <span className="inline-flex items-center gap-1.5 text-[#344054]">
          {current && <span className={THIS_TAG_CSS}>{ctx.grain === 'weekly' ? 'This week' : 'This month'}</span>}
          <span>{main}</span>
        </span>
        {!hideDates && <span className="text-[11px] font-medium text-[#98A2B3]">{sub}</span>}
      </div>
    </th>
  );
};

/** The span periods (weeks or months) no column carries, over every row shown (sorted, distinct); empty without per-period OTB lines. */
const laterPeriodsOf = (flatRows: FlatRow[], lines: MatrixLine[], periods: number[], ctx: CellContext): number[] => {
  if (!lines.some((l) => isPeriodOtb(l.metric))) return [];
  const later = new Set<number>();
  flatRows.forEach((fr) => {
    if (fr.kind === 'skuPlaceholder') return;
    laterOtb(fr.row, 'otbPeriod', periods, ctx)?.periods.forEach((p) => later.add(p));
  });
  return Array.from(later).sort((a, b) => a - b);
};
/** Monthly: "Jan 2027" or "Jan–Feb 2027". Weekly: "Jan 17–23, 2027" or "Jan 17 – Feb 20, 2027" (the first week's Sunday to the last week's Saturday). */
export const laterRangeLabel = (periods: number[], grain: Grain): string => {
  const first = periods[0];
  const last = periods[periods.length - 1];
  if (grain === 'weekly') {
    if (periods.length === 1) return periodFullName(first, grain);
    const s = epochWeekStart(first);
    const e = epochWeekEnd(last);
    return `${MONTHS[s.getUTCMonth()]} ${s.getUTCDate()} – ${MONTHS[e.getUTCMonth()]} ${e.getUTCDate()}, ${e.getUTCFullYear()}`;
  }
  return periods.length === 1 ? periodFullName(first, grain) : `${periodFullName(first, grain).split(' ')[0]}–${periodFullName(last, grain)}`;
};

export interface PlanOtbMatrixProps { model: WeeklyPlanningReportModel }

export const PlanOtbMatrix: React.FC<PlanOtbMatrixProps> = ({ model }) => {
  const { payload, ctx, flatRows } = model;
  const options = optionsOf(model);
  const periods = useMemo(() => (payload && ctx ? displayPeriods(payload.periods, ctx.targetPeriod, options.showPrevious) : []), [payload, ctx, options.showPrevious]);
  const lines = useMemo(() => linesFor(options, periodOtbAvailable(payload), ctx?.grain), [options, payload, ctx?.grain]);
  if (!payload || !ctx || flatRows.length === 0) return <div className={CARD_CSS}><TableStates state="empty" columns={4} /></div>;
  const later = laterPeriodsOf(flatRows, lines, periods, ctx);
  const withLater = later.length > 0;
  const currentPeriod = ctx.currentPeriod ?? ctx.targetPeriod;
  const hierarchy = HIERARCHY_BY_KEY[model.view.state.view];
  const parents = childClassParents(flatRows);
  // What-if Plan Sales edits (retail only: at cost the sales plan has no figure to edit).
  const grain = ctx.grain;
  const edits = model.view.state.planEdits?.[grain] ?? {};
  const editCount = planEditCount(edits);
  const planEdit: PlanEditProps | undefined = ctx.basis === 'cost' ? undefined : {
    originals: model.planEditOriginals ?? {},
    raised: model.planEditRaised ?? {},
    changed: model.planEditChanged ?? {},
    onEdit: (rowKey, period, value) => model.view.set({ planEdits: { ...model.view.state.planEdits, [grain]: withPlanEdit(edits, rowKey, period, value) } }),
  };
  const clearEdits = () => model.view.set({ planEdits: { ...model.view.state.planEdits, [grain]: {} } });
  return (
    <div className={CARD_CSS}>
      {editCount > 0 && (
        <div role="status" className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b border-[#E9D7FE] bg-[#F9F5FF] text-[12px] text-[#53389E]">
          <span className="inline-flex items-center gap-1.5">
            <PencilLine className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            {/* the bar says how the other plan lines follow the edit (the rules in kit/logic/planEdits.ts). */}
            <span><strong>What-if:</strong> {editCount} plan sales {editCount === 1 ? 'edit' : 'edits'}. Plan inflows, {periodLabels(grain).bom}, markdowns, OTB and every total are recalculated from {editCount === 1 ? 'it' : 'them'}; markdowns keep their % of sales, {periodLabels(grain).bom} keeps its stock-to-sales, inflows and OTB follow; a negative inflow is taken off the period before (Plan Builder's rule).{grain === 'weekly' ? ' Stock moves ahead of the edited week.' : ''} The plan itself is unchanged. Saved with the view.</span>
          </span>
          <button type="button" onClick={clearEdits} className="shrink-0 font-semibold text-[#6941C6] hover:text-[#53389E] underline-offset-2 hover:underline">Clear edits</button>
        </div>
      )}
      <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-220px)]">
        <table className="w-full text-left text-[13px] border-collapse">
          <thead className="sticky top-0 z-20 bg-[#F9F9FB]">
            <tr className={HEADER_ROW_CSS}>
              <th scope="col" className={cn(HEAD_CELL_CSS, 'left-0 z-30 text-left border-r border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0]')} style={{ width: LABEL_WIDTH, minWidth: LABEL_WIDTH }} title={hierarchy.description}>
                {hierarchy.firstColumn}
              </th>
              <th scope="col" style={METRIC_STYLE} className={cn(HEAD_CELL_CSS, 'z-30 text-left')}>Metric</th>
              <th scope="col" style={TYPE_STYLE} className={cn(HEAD_CELL_CSS, 'z-30 text-left', TYPE_EDGE_CSS)}>Type</th>
              {periods.map((p) => <PeriodHeaderCell key={p} period={p} ctx={ctx} current={p === currentPeriod} hideDates={options.hidePeriodDates} />)}
              {withLater && (
                // the running line does not add up, so the tooltip says what Later holds on each line.
                <th scope="col" className={cn(HEAD_CELL_CSS, 'text-right')} title={`OTB by ${periodNoun(ctx.grain)} of the span's later ${periodNoun(ctx.grain)}s; the Running OTB there is the span's final figure`}>
                  <div className="flex flex-col items-end gap-0.5 leading-tight">
                    <span className="text-[#344054]">Later</span>
                    {!options.hidePeriodDates && <span className="text-[11px] font-medium text-[#98A2B3]">{laterRangeLabel(later, ctx.grain)}</span>}
                  </div>
                </th>
              )}
              <th scope="col" className={cn(HEAD_CELL_CSS, 'text-right', TOTAL_CSS)}>Total</th>
            </tr>
          </thead>
          {flatRows.map((fr) =>
            fr.kind === 'skuPlaceholder' ? (
              <SkuPlaceholderBlock key={fr.id} flatRow={fr} colSpan={periods.length + 3 + (withLater ? 1 : 0)} onExpandSkus={model.loadChildRows} />
            ) : (
              <MatrixBlock key={fr.id} flatRow={fr} childOf={parents.get(fr.id)} planEdit={planEdit} withLater={withLater} lines={lines} periods={periods} ctx={ctx} options={options} currentPeriod={currentPeriod} onToggleGroup={model.toggleGroup} />
            ),
          )}
        </table>
      </div>
    </div>
  );
};

// ---- CSV ----------------------------------------------------------------------------------------

const labelCsv = (text: string, depth: number): string => csvCell('  '.repeat(depth) + (CSV_FORMULA_RE.test(text) ? `'${text}` : text), false);
const numCsv = (n: number | null): string => (n === null ? '' : csvNumber(n));
const TRENDED_TYPE = 'Trended';
const BENCH_CSV_HEAD = ['Benchmark own', 'Benchmark peers', 'Benchmark index', 'Unit', 'Benchmark basis'];
const BENCH_CSV_BLANK = BENCH_CSV_HEAD.map(() => '');

/** `includeExpanded` as in the kit's CSV: true = every flat row shown plus a "Level" column; false = top-level rows only; absent = the rows as shown, no Level column. */
/** `allLines` (the Export dialog's "All lines"): every metric's Plan and Act lines and the LY lines, whatever the mode, Rows and options on screen. */
export interface PlanOtbCsvOptions { includeExpanded?: boolean; allLines?: boolean }

/**
 * The matrix as numbers: label (indented two spaces per depth; "Level" after it when
 * `includeExpanded` is true), Metric, Type, each period, Total. Every value is a plain number (no "$", no separators, ASCII minus), blank
 * where the matrix shows "—" or "No weekly plan". SKU placeholders are skipped. every line is dollars, so the Total
 * header names the basis ("Total (cost)"). Sales Act is the actual to date; in a window with a trend period each row gets a "Sales
 * Trended" line (Type "Trended") right after it: the trended sales in the trend period, the actual in the others, Total = the
 * matrix's "incl. trend" Total.
 */
export const buildPlanOtbCsv = (model: WeeklyPlanningReportModel, csvOptions: PlanOtbCsvOptions = {}): string => {
  const { payload, ctx } = model;
  if (!payload || !ctx) return '';
  const shownOptions = optionsOf(model);
  const options = csvOptions.allLines ? { ...defaultPlanOtbOptions(ctx.grain), showPrevious: shownOptions.showPrevious, showBenchmarks: shownOptions.showBenchmarks, showLy: true } : shownOptions;
  const periods = displayPeriods(payload.periods, ctx.targetPeriod, options.showPrevious);
  const lines = linesFor(options, periodOtbAvailable(payload), ctx.grain, { lyLines: true }); // the CSV keeps LY as its own numeric lines
  const trendPeriods = (payload.trend?.periods ?? []).filter((p) => p === (ctx.currentPeriod ?? ctx.targetPeriod)); // the current period only (planOtbCells trendedHere)
  const suffix = basisSuffix(ctx.basis);
  const withTrended = payload.trend?.fallbackBasis !== 'projected' && periods.some((p) => trendPeriods.includes(p)) && lines.some((l) => l.metric === 'sales' && l.type === 'act');
  const withLevel = csvOptions.includeExpanded === true;
  const laterPeriods = laterPeriodsOf(model.flatRows, lines, periods, ctx);
  const withLater = laterPeriods.length > 0;
  const shown = model.flatRows.filter((fr) => fr.kind !== 'skuPlaceholder' && (csvOptions.includeExpanded !== false || fr.depth === 0));
  const childParents = childClassParents(model.flatRows);
  // (review): the benchmark is numbers, not prose: one row per metric, with Own / Peers / Index and a Unit column
  // after Total. The columns exist only when some shown block has a benchmark (Options › Show benchmarks).
  const benchBlocks = new Map(shown.map((fr) => [fr.id, benchmarkCsvBlock(fr, ctx, options)]));
  const withBench = Array.from(benchBlocks.values()).some((x) => x !== null);
  const head = [HIERARCHY_BY_KEY[model.view.state.view].firstColumn, ...(withLevel ? ['Level'] : []), 'Metric', 'Type', ...periods.map((p) => csvPeriodHeader(p, ctx, trendPeriods)), ...(withLater ? [`Later (${laterRangeLabel(laterPeriods, ctx.grain)})`] : []), `Total${suffix}`, ...(withBench ? BENCH_CSV_HEAD : [])];
  const header = head.map((h) => csvCell(h, true)).join(',');
  const body = shown.flatMap((fr) => {
    const csvLine = (line: MatrixLine, type: string, nums: (number | null)[], total: number | null): string => [
      labelCsv(rowLabelText(fr), fr.depth),
      ...(withLevel ? [String(fr.depth)] : []),
      csvCell(metricLabel(line.metric, ctx.grain), true),
      csvCell(type, true),
      ...nums.map(numCsv),
      ...(withLater ? [numCsv(isPeriodOtb(line.metric) ? laterOtb(fr.row, line.metric, periods, ctx)?.value ?? null : null)] : []),
      numCsv(total),
      ...(withBench ? BENCH_CSV_BLANK : []),
    ].join(',');
    // One Benchmark row per metric (numbers: own, peers, index, unit); a block with no peer figures gets one row saying why.
    const block = benchBlocks.get(fr.id) ?? null;
    const benchRow = (type: string, vals: string[]): string => [
      labelCsv(rowLabelText(fr), fr.depth),
      ...(withLevel ? [String(fr.depth)] : []),
      csvCell('Benchmark', true),
      csvCell(type, true),
      ...periods.map(() => ''),
      ...(withLater ? [''] : []),
      '',
      ...vals,
    ].join(',');
    const benchCsv = block === null ? [] : block.note !== null
      ? [benchRow(block.note, ['', '', '', '', csvCell(block.basis, true)])]
      : block.rows.map((r) => benchRow(r.label, [numCsv(r.own), numCsv(r.peer), numCsv(r.index), csvCell(r.unit, true), csvCell(block.basis, true)]));
    // A child class unrolled from its parent exports its actuals only, as the matrix shows it.
    const blockLines = childParents.has(fr.id) ? lines.filter((l) => l.type === 'act') : lines;
    return [...blockLines.flatMap((line) => {
      const { cells, total, includesTrend } = lineCells(fr.row, line, periods, ctx, options);
      const nums = cells.map((c) => lineCellNumber(fr.row, line, c, ctx));
      const salesAct = line.metric === 'sales' && line.type === 'act';
      // The Act line's Total is the actuals' sum; the "incl. trend" Total belongs to the Trended line.
      const out = [csvLine(line, typeLabelOf(line), nums, salesAct && includesTrend ? sumLineNumbers(nums, cells) : total)];
      if (salesAct && withTrended) {
        const trended = cells.map((c) => trendedLineNumber(fr.row, line, c, ctx, trendPeriods));
        out.push(csvLine(line, TRENDED_TYPE, trended, sumLineNumbers(trended, cells)));
      }
      return out;
    }), ...benchCsv];
  });
  return [header, ...body].join('\r\n');
};

export const exportPlanOtbCsv = (model: WeeklyPlanningReportModel, csvOptions?: PlanOtbCsvOptions): void => downloadCsv(buildPlanOtbCsv(model, csvOptions), csvFileName('plan_otb', model.view.state));


// Plan & OTB's page config (Plan 4 spec 7.2). A module-level constant: the page shell
// memoises on its identity. KPIs and charts read `model.totalsRows` through `totalRows` (SKU rows
// never count, so loading SKUs never changes a KPI). Mode / Rows / Options write `state.planOtb`,
// which only reshapes the matrix (no request).
//
// Decisions recorded here:
// - OTB Inflow Plan = Σ summary.inflowPlanSpan over the counted rows with an OTB (review), so it reconciles
// with Total OTB (− received − on order) on both grains; the OTB trend bars still show Σ inflowPlan per span period. Total Sales Plan
// = Σ salesPlan over the periods from the target on ("next N weeks / months"), never the Show previous months.
// - "OTB trend": one bar per MONTH of the span (Σ OTB of that month over the counted rows with an OTB, `kpiTotals(…).otbByPeriod`)
// and the running OTB as the bold line, so every span month shows (Jan no longer blank) and the last running = Total OTB. Weekly groups its
// weeks by month. Retail only: at cost, or without per-month lines, it falls back to the old chart (inflow plan per span period and one OTB point).
// - The Rows dropdown commits on close (FilterDropdown); with nothing ticked it reads "Sales only".
// - Benchmarks: none of Total OTB / Planned inflows / Total Sales Plan is a benchmark metric, so a fifth KPI
// "Stock-to-sales vs peers" carries the vertical's G4 stock-to-sales (value), your plan's and the actual benchmark stock-to-sales
// in the benchmark month (sub-line) and turnover on its benchmark line. The plan figure is Σ plan BOM inventory ÷ Σ plan sales
// at the target period over the counted rows (sum then divide, "—" when either sum is not positive); in Weekly it is one
// week's sales, so it is labelled "(weekly)" and the tooltip says it is not the monthly benchmark's basis. It reads
// `ctx.benchmarks.client` and `totalsRows` only, so it never moves while SKU or product rows load. The OTB trend (plan
// dollars) and Sales variance charts have no benchmark measure and get no marker; the dashboard ends with "You vs peers".
// Each class and location block of the matrix gets one "Benchmark" line behind Options › Show benchmarks (off by default).
// - The matrix ignores the catalogue columns, so `hideColumnControls` drops Columns / % of Total and
// How it works describes the matrix: Metric, Type, Periods, Total, Mode, then the metrics shown.

const setOptions = (m: WeeklyPlanningReportModel, patch: Partial<PlanOtbOptions>): void => m.view.set({ planOtb: { ...optionsOf(m), ...patch } });
const periodWord = (grain: string, n: number): string => (grain === 'monthly' ? (n === 1 ? 'month' : 'months') : n === 1 ? 'week' : 'weeks');

/**
 * Σ over the counted rows of Σ measure over the given periods, on the basis; null when nothing
 * is known, and `available` false on the cost basis when some cell has the retail plan but no cost (sales plans never do).
 */
const sumOver = (m: WeeklyPlanningReportModel, measure: 'inflowPlan' | 'salesPlan', periods: number[]): { value: number | null; available: boolean } => {
  const basis = m.view.state.basis;
  const t = costTotal(totalRows(m.totalsRows).flatMap((f) => periods.map((p) => valueOnBasis(f.row.periods[String(p)], measure, basis))));
  return { value: t.value, available: t.available };
};

/** the Total Sales Plan KPI's periods: the target period on (never the Show previous ones). */
const targetOnPeriods = (m: WeeklyPlanningReportModel): number[] =>
  m.payload && m.ctx ? displayPeriods(m.payload.periods, m.ctx.targetPeriod, false) : [];

/** Σ deviation (plan BOM − actual BOM at the span's start) over the counted rows that have an OTB + Dev; null when any lacks it. */
const deviationTotal = (m: WeeklyPlanningReportModel): number | null => {
  const rows = totalRows(m.totalsRows).filter((f) => isNum(f.row.summary?.otbDev));
  if (rows.length === 0 || rows.some((f) => !isNum(f.row.summary?.deviation))) return null;
  return rows.reduce((t, f) => t + (f.row.summary?.deviation as number), 0);
};

/** Σ plan BOM inventory ÷ Σ plan sales at the target period over the counted rows; null unless both sums are positive. */
export const planStockToSales = (m: WeeklyPlanningReportModel): number | null => {
  const { ctx } = m;
  if (!ctx) return null;
  let inventory = 0;
  let sales = 0;
  totalRows(m.totalsRows).forEach((f) => {
    const c = f.row.periods[String(ctx.targetPeriod)];
    if (isNum(c?.inventoryPlan)) inventory += c.inventoryPlan;
    if (isNum(c?.salesPlan)) sales += c.salesPlan;
  });
  return inventory > 0 && sales > 0 ? inventory / sales : null;
};

/**
 * "Stock-to-sales vs peers": the vertical's value, your plan (Monthly only) and the actual in the benchmark month, turnover on the benchmark line;
 * null without a client value. Your actual is summed over the rows the report counts (`benchmarkForRows`, the same classes as the KPIs beside it),
 * or is the client-level figure, said so, where the rows cannot give it. A Weekly plan is one week of sales, so its stock-to-sales is not shown
 * next to a monthly peer value.
 */
export const benchmarkKpi = (m: WeeklyPlanningReportModel): KpiItem | null => {
  const { ctx } = m;
  const b = ctx?.benchmarks;
  const r = ctx ? benchmarkForRows(m.totalsRows, ctx) : null;
  if (!ctx || !b || !r) return null;
  const c = r.value;
  const month = benchmarkMonthEnd(b);
  const peerSs = gatedPeerValue(c.peer, 'stockToSales');
  const idx = benchmarkIndex('stockToSales', c.own.stockToSales, peerSs);
  const weekly = ctx.grain === 'weekly';
  const plan = weekly ? null : planStockToSales(m);
  const clientLevel = r.scope === 'client';
  const turnoverIdx = benchmarkIndex('turnover', c.own.turnover, gatedPeerValue(c.peer, 'turnover'));
  const actual = `actual ${formatBenchmark('stockToSales', c.own.stockToSales)} in ${month}${ctx.basis === 'cost' ? ' (retail)' : ''} (index ${formatIndex('stockToSales', idx)})${clientLevel ? ' · client level' : ''}`;
  return {
    label: kpiLabel('Stock-to-sales vs peers', { filtered: clientLevel && c.filtered, basis: ctx.basis }),
    value: peerSs === null ? '—' : formatBenchmark('stockToSales', peerSs),
    sub: c.status === 'ok' ? (weekly ? actual : `your plan ${plan === null ? '—' : plan.toFixed(2)} · ${actual}`) : c.peer ? PEER_TEXT_NOT_ENOUGH : PEER_TEXT_NO_GROUP,
    tone: indexTone('stockToSales', idx) === 'bad' ? 'warn' : 'neutral',
    tooltip: retailTooltip(`Peers' benchmark stock-to-sales (beginning inventory ÷ the month's sales), ${c.peerLabel}. ${weekly ? 'Peer and actual figures are for the benchmark month; a weekly plan is one week of sales, so no plan figure is shown against them.' : 'Your plan: plan BOM inventory ÷ plan sales at the target period, over the classes counted.'} ${clientLevel ? 'Client level: every class passing the scope filters, not only the rows shown.' : 'Your actual covers the same classes as the other KPIs.'}`, ctx.basis),
    benchmark: { text: `${retailTag('Turnover', ctx.basis)} you ${formatBenchmark('turnover', c.own.turnover)} · peers ${peerText(c.peer, 'turnover')}${c.status === 'ok' ? ` (index ${formatIndex('turnover', turnoverIdx)})` : ''} · ${b.label ?? ''}` },
  };
};

const kpis = (m: WeeklyPlanningReportModel): KpiItem[] => {
  const { ctx, payload } = m;
  if (!ctx || !payload) return [];
  const t = kpiTotals(m.totalsRows, ctx);
  const span = payload.spanPeriods;
  const ahead = targetOnPeriods(m);
  const cost = m.view.state.basis === 'cost';
  const salesPlan = sumOver(m, 'salesPlan', ahead);
  // the OTB + Dev sub-line spells the figure out ("OTB $448 − $16,437 over plan BOM") where every counted row has a deviation.
  const otbDevSub = cost ? null : otbDevText(t.otb, deviationTotal(m), ctx.grain);
  // QA fix 1: the received and on order (in the Planned inflows tooltip) are over the OTB-bearing classes only (so it reconciles with the OTB);
  // the on order of classes without an OTB is named once, as its own clause.
  const excludedOnOrder = isNum(t.onOrderNoOtb) && t.onOrderNoOtb > 0 ? ` · + ${fmtCurrency(t.onOrderNoOtb)} on order on classes without a plan` : '';
  const bench = benchmarkKpi(m);
  return [
    {
      // with a later start the label names the window the payload counted ("Jan–Apr 2027") instead of the span.
      label: `Open to buy (${(otbStartFor(m.view.state.otbStart, ctx.grain, payload.targetPeriod) !== null ? otbWindowText(span, ctx.grain) : null) ?? otbSpanText(m.view.state)} · ${cost ? 'cost' : 'retail'})`, value: fmtCurrency(t.otb),
      sub: !cost ? 'over the span' : t.notAtCost.includes('otb') ? OTB_NOT_AT_COST : 'over the span, at cost',
      ...otbCoverageNoteProp(t.otbPartial, m.payload?.spanPeriods, ctx.grain),
      // the formula lives in the tooltip, not the sub line.
      tooltip: `Open to buy: planned inflows − received to date − on order over the span${cost ? ', every figure at cost' : ''}. ${otbExclusionNote(t.otbExcluded)}`,
    },
    { label: 'OTB + Dev', value: fmtCurrency(t.otbDev), sub: cost ? OTB_DEV_NOT_AT_COST : otbDevSub ?? 'OTB + (plan BOM − actual BOM)', tooltip: `${OTB_DEV_TOOLTIP} Totals the same classes as OTB.` },
    // review: the inflow plan behind Total OTB (Σ summary.inflowPlanSpan of the rows with an OTB), so
    // OTB Inflow Plan − received − on order = Total OTB on either grain.
    {
      label: 'Planned inflows', value: fmtCurrency(t.otbInflowPlan),
      sub: cost && t.notAtCost.includes('otbInflowPlan') ? OTB_NOT_AT_COST : `over the OTB span (${span.length} ${periodWord(ctx.grain, span.length)})`,
      // the received / on order amounts it reconciles with moved from the sub line into the tooltip.
      tooltip: `The planned inflows behind open to buy: planned inflows − received to date (${fmtCurrency(t.otbReceived)}) − on order (${fmtCurrency(t.otbOnOrder)}) = open to buy${cost ? ', every figure at cost' : ''}. Classes without an OTB (no plan, PNOTB / NOTB) are left out of all three figures${excludedOnOrder ? `; their on order (${fmtCurrency(t.onOrderNoOtb)}) is shown separately` : ''}.`,
    },
    {
      label: 'Total Sales Plan', value: fmtCurrency(salesPlan.value),
      sub: salesPlan.available ? `next ${ahead.length} ${periodWord(ctx.grain, ahead.length)}` : 'Not available at cost',
      ...(salesPlan.available ? {} : { tooltip: NOT_AT_COST_TOOLTIP }),
    },
    ...(bench ? [bench] : []),
  ];
};

const TREND_SERIES: ChartSeries[] = [
  { key: 'value', label: 'Planned inflows', kind: 'bar', color: '#9E77ED' },
  { key: 'otb', label: 'OTB', kind: 'line', color: '#101828', bold: true },
];
const VARIANCE_SERIES: ChartSeries[] = [{ key: 'value', label: 'Sales vs compare', kind: 'bar', color: '#2E90FA' }];

const OTB_TREND_SERIES: ChartSeries[] = [
  { key: 'value', label: 'OTB', kind: 'bar', color: '#9E77ED' },
  { key: 'otb', label: 'Running OTB', kind: 'line', color: '#101828', bold: true },
];

/** The OTB trend chart's data: per period (week / month) OTB (bars) and running OTB (line) from the counted rows' lines; null when there are none. */
export const otbTrendData = (m: WeeklyPlanningReportModel): { name: string; value: number; otb: number }[] | null => {
  const { ctx } = m;
  const lines = ctx ? kpiTotals(m.totalsRows, ctx).otbByPeriod : null;
  return lines && ctx ? lines.map((l) => ({ name: ctx.grain === 'weekly' ? formatPeriodLabel(l.period, 'weekly') : monthYear(l.period), value: l.otb, otb: l.running })) : null;
};

const dashboardCards = (m: WeeklyPlanningReportModel): React.ReactNode[] => {
  const { ctx, payload } = m;
  if (!ctx || !payload) return [];
  const byPeriod = otbTrendData(m);
  const noun = periodNoun(ctx.grain);
  const trend = byPeriod ?? [
    ...payload.spanPeriods.map((p) => ({ name: formatPeriodLabel(p, ctx.grain), value: sumOver(m, 'inflowPlan', [p]).value, otb: null })),
    { name: 'OTB', value: null, otb: kpiTotals(m.totalsRows, ctx).otb },
  ];
  return [
    <ChartCard
      key="trend"
      title="OTB trend"
      caption={byPeriod ? `Bars: open to buy per ${noun} of the span · line: running OTB (${RUNNING_OTB_RULE})` : `Bars: planned inflows per ${noun} over the span · line: open to buy${m.view.state.basis === 'cost' ? ` (at cost; OTB by ${noun} is retail only)` : ''}`}
      data={trend}
      series={byPeriod ? OTB_TREND_SERIES : TREND_SERIES}
      xKey="name"
      formatValue={fmtCurrency}
    />,
    <ChartCard
      key="variance"
      title="Sales variance by class"
      caption={`Target period sales (trended sales in a period still in progress) − the compare value${m.view.state.basis === 'cost' ? ' (retail: no sales at cost)' : ''}`}
      data={chartRowsByClass(m.totalsRows, ctx, 'salesVariance', m.view.state.view)}
      series={VARIANCE_SERIES}
      xKey="name"
      sortable
      formatValue={fmtDiffCurrency}
    />,
    // plan sales ahead vs LY over the counted classes (kit/summary/salesAheadCard.tsx).
    salesAheadDashboardCard(m),
    benchmarkDashboardCard(m, ['turnover', 'stockToSales', 'markdownPct', 'grossMarginPct', 'cashMarginPct', 'salesChangeLy']),
  ].filter((card) => card !== null);
};

const MODE_OPTIONS: { value: PlanOtbMode; label: string }[] = [{ value: 'detailed', label: 'Detailed' }, { value: 'basic', label: 'Basic' }, { value: 'otb', label: 'OTB' }];
const ROW_CODES: PlanOtbRow[] = ['inflow', 'inventory', 'markdowns', 'onOrder', 'otb'];
const ROW_OPTIONS: FilterOption[] = ROW_CODES.map((code) => ({ code, label: METRIC_LABELS[code] }));
type OptionKey = 'showPrevious' | 'hideFutureActuals' | 'hidePeriodDates' | 'hideRowColours' | 'showBenchmarks' | 'showLy';
const OPTION_KEYS: OptionKey[] = ['showPrevious', 'showLy', 'hideFutureActuals', 'hidePeriodDates', 'hideRowColours', 'showBenchmarks'];
/** "Show benchmarks" is offered only to a client with a benchmark level (not 'none'). */
const optionKeysFor = (m: WeeklyPlanningReportModel): OptionKey[] => OPTION_KEYS.filter((k) => k !== 'showBenchmarks' || m.benchmarkLevel !== 'none');
const optionLabel = (key: OptionKey, grain: string): string =>
  ({ showPrevious: `Show previous 4 ${grain === 'monthly' ? 'months' : 'weeks'}`, hideFutureActuals: 'Hide future periods', hidePeriodDates: 'Hide period dates', hideRowColours: 'Hide row colours', showBenchmarks: 'Show benchmarks', showLy: 'Show last year (LY)' })[key];

export const LY_OTB_MODE_HINT = 'LY shows in the Act lines in Detailed and Basic mode (Basic: Sales only). OTB mode shows the OTB lines only.';

/** Mode, Rows and the Options popover; all write `state.planOtb`. */
export const PlanOtbViewControls: React.FC<{ model: WeeklyPlanningReportModel }> = ({ model }) => {
  const [open, setOpen] = useState(false);
  const o = optionsOf(model);
  const keys = optionKeysFor(model);
  const on = keys.filter((k) => o[k]).length;
  return (
    <>
      <SegmentedControl<PlanOtbMode> ariaLabel="Mode" size="sm" options={MODE_OPTIONS} value={o.mode} onChange={(mode) => setOptions(model, { mode })} />
      <FilterDropdown
        label="Rows"
        options={ROW_OPTIONS}
        searchable={false}
        allLabel="Sales only"
        selection={{ codes: o.rows, exclude: false }}
        onApply={(s) => setOptions(model, { rows: ROW_CODES.filter((r) => s.codes.includes(r)) })}
      />
      <Popover label="Options" isOpen={open} onOpenChange={setOpen} badge={on + (model.view.state.showChildClasses ? 0 : 1)} align="right">
        <div className="flex flex-col gap-3">
          {/* Wendi (2026-10-22), item 2: child classes are an option (view state, saved in views). */}
          <SwitchFilter label={SHOW_CHILD_CLASSES_LABEL} hint={SHOW_CHILD_CLASSES_HINT} checked={model.view.state.showChildClasses} onChange={(showChildClasses) => model.view.set({ showChildClasses })} />
          {keys.map((k) => {
            // OTB mode has no Sales / Inflows / Markdowns / BOM lines, so LY has nothing to sit under: disabled there, and it says why.
            const lyIdle = k === 'showLy' && o.mode === 'otb';
            return (
              <SwitchFilter
                key={k}
                label={optionLabel(k, model.view.state.grain)}
                checked={o[k]}
                disabled={lyIdle}
                hint={lyIdle ? LY_OTB_MODE_HINT : undefined}
                onChange={(v) => setOptions(model, { [k]: v } as Partial<PlanOtbOptions>)}
              />
            );
          })}
        </div>
      </Popover>
    </>
  );
};

const TREND_ITEM: HowItWorksItem = {
  label: 'Sales Act (period in progress)',
  description:
    'Sales so far, with the trended sales under it: where the period is heading, worked out from sales so far and the % of the period elapsed. ' +
    'It is coloured against the full-period plan (retail); hover for how it was worked out, and the CSV marks that period "(trend)".',
};
const MATRIX_ITEMS: HowItWorksItem[] = [
  { label: 'Metric', description: 'The measure on the line; each row of the table shows its block of metric lines.' },
  { label: 'Type', description: 'Plan or Act (actual). On Order has only an Act line; the OTB lines are worked out over the span and carry no type.' },
  //: no OTB line of its own; the span's OTB is the OTB by week / by month Total.
  { label: 'OTB', description: 'The span\'s open to buy (planned inflows − received to date − on order) is the Total of the OTB by week (or by month) line; there is no separate OTB line. OTB + Dev sits beside it in the Total column. PNOTB / NOTB classes have none.' },
  // Hide future periods keeps the current period; the BOM Total is the current period on Plan and Act alike; the Act Total says
  // how many of the Plan Total's periods it adds; Later's Running OTB is the span's final figure, not a sum.
  { label: 'Periods', description: 'One column per week or month from the selected period on (Show previous adds the four before it); the current period is highlighted. Actuals show through the current period; Hide future periods keeps the current period and shows nothing (not even LY) in the periods after it.' },
  { label: 'Total', description: 'Plan and Act summed over the periods shown; an Act Total adds the actuals so far, and its tooltip says how many of the Plan Total\'s periods that is. BOM inventory is not a sum: its Total is the current period\'s value on the Plan and Act lines alike, so the two compare at the same period.' },
  { label: 'Later', description: 'Appears when the open-to-buy span runs past the last period column: the OTB by period of those later weeks (or months), so the columns and Later add up to the OTB by period Total, the span\'s OTB; the Running OTB there is the span\'s final figure.' },
  { label: 'Mode', description: 'Detailed shows Sales and the selected Rows; Basic shows Sales and the OTB lines (OTB + Dev, OTB by week or by month, Running OTB); OTB shows the OTB lines only.' },
  {
    label: 'What-if plan sales',
    description: 'Click a Plan Sales cell in the current period or later and type a figure (only plan sales can be edited): markdowns keep their % of sales, BOM its stock-to-sales, inflows and OTB follow, and a negative inflow is taken off the period before. Every cell the edit moved carries a small pencil whose tooltip gives the plan figure; lowered / raised BOM cells carry an arrow. Saved with the view, never written to the plan.',
  },
];
const LY_ITEM: HowItWorksItem = {
  label: 'Last year (LY)',
  description:
    'Options › Show last year (LY): in the Act lines of sales, inflows and markdowns (and the start-of-period inventory in Monthly), future periods show last year\'s figure in place, marked "LY", and the current period shows "LY $X" small under its actual. ' +
    'Weeks line up by weekday (the same week last year, 364 days back); months by month. Hover an Act cell for its variance against LY; where no plan colours the cell, it is coloured against LY. The Act Total stays actuals only. LY is retail only, and the CSV carries LY as its own lines.',
};
const BENCHMARK_ITEM: HowItWorksItem = {
  label: 'Benchmark line',
  description: 'Options › Show benchmarks: per class (vs peers in its master class) and per location (vs the vertical), turnover, stock-to-sales and markdown % for the benchmark month, with the index (you ÷ peers). Peer figures are ratios, so they do not change with the cost basis; vendor, product and SKU rows have none.',
};
const METRIC_ITEMS: Record<PlanOtbMetric, string> = {
  sales: 'Plan sales and net sales in the period. Act is green at or above plan, red below.',
  inventory: 'Plan BOM inventory and actual BOM inventory at the start of the period. Act is green within ±10% of plan, amber within ±25%, red beyond.',
  // inflows use the inventory band (far under plan is a miss too); markdowns are never red for being under plan.
  inflow: 'Planned and actual inflows in the period. Act is green within ±10% of plan, amber within ±25%, red beyond.',
  markdowns: 'Planned and actual markdown dollars in the period. Act is green at or below plan, amber up to 25% over plan, red beyond.',
  onOrder: 'Open purchase orders, shown once in the selected period and the Total (actuals only).',
  otb: 'Open to buy over the span: planned inflows over the span − received to date − on order. The Total of the OTB by week (or by month) line. PNOTB / NOTB classes have none.',
  otbDev: `${OTB_DEV_TOOLTIP} Plan and actual BOM are the span's first period. Shown in the Total column only; the OTB itself is the OTB by week (or by month) Total.`,
  otbPeriod: `Open to buy for each week or month of the span, in its own column: its planned inflows − received to date (first week or month only) − on order due in it. Weeks (or months) past the last column are added up in the Later column. The Total is the span's OTB (planned inflows − received − on order). ${RUNNING_OTB_RULE} At cost: not available. ${ON_ORDER_TIMING_CAVEAT}`,
  otbRunning: `Cumulative OTB through each week or month of the span; the last equals the span's OTB. ${RUNNING_OTB_RULE} At cost: not available. ${ON_ORDER_TIMING_CAVEAT}`,
};
const howItWorksColumns = (m: WeeklyPlanningReportModel): HowItWorksItem[] => {
  const metrics = linesFor(optionsOf(m), periodOtbAvailable(m.payload), m.ctx?.grain).map((l) => l.metric).filter((x, i, a) => a.indexOf(x) === i);
  return [...MATRIX_ITEMS, ...(optionsOf(m).showLy ? [LY_ITEM] : []), TREND_ITEM, ...metrics.map((x) => ({ label: metricLabel(x, m.ctx?.grain), description: METRIC_ITEMS[x] })), ...(m.benchmarkLevel !== 'none' ? [BENCHMARK_ITEM] : [])];
};

export const PLAN_OTB_CONFIG: ReportPageConfig = {
  reportKey: 'plan_otb',
  title: 'Plan & OTB',
  subtitle: 'Plan against actuals by period, with open-to-buy.',
  icon: <Target className="w-5 h-5" aria-hidden="true" />,
  howItWorksIntro:
    'Plan & OTB lays out, for each class, the plan next to what actually happened, period by period: sales, BOM inventory, inflows and markdowns, ' +
    'plus on order and open to buy (OTB) for the span you picked. Actuals are green or red against plan and show last year\'s figure (with Show LY) or "—" for periods still to come. ' +
    'OTB is worked out once for the whole span (planned inflows − received to date − on order): it is the Total of the OTB by week / by month line, whose columns carry the span\'s weeks (or months) (a Later column holds those past the last column, so the columns add up to the Total); Running OTB adds them up and OTB + Dev sits beside the Total. OTB mode always shows the OTB lines, whatever the Rows selection. ' +
    'While the period is still in progress, the Sales Act cell shows sales so far with the trended sales under it. ' +
    'Pick a hierarchy to see classes by category or by location. Open a parent class to see its child classes, marked "Child of" the parent.',
  kpis,
  dashboardCards,
  extraViewControls: (m) => <PlanOtbViewControls model={m} />,
  renderTable: (m) => <PlanOtbMatrix model={m} />,
  // class views only (the matrix lays out class plans); no vendor or product hierarchies.
  supportsVendorView: false,
  exportCsv: exportPlanOtbCsv,
  hideColumnControls: true,
  howItWorksColumns,
};

// Plan & OTB (spec 7.2), rebuilt on the kit (Plan 4 ): the shared page shell with this
// report's config, whose table is the period matrix (PlanOtbMatrix). The exported names and props are
// the ones the two mounting pages use (ClientAnalytics, ClientDashboardV3Reports).

export interface PlanOTBReportProps {
  clientId: string | number;
  epochMonth: number | null;
  setEpochMonth?: (epochMonth: number) => void;
}

export const PlanOTBReport: React.FC<PlanOTBReportProps> = (props) => <WeeklyPlanningReportPage config={PLAN_OTB_CONFIG} {...props} />;

export interface PlanOTBReportModalProps {
  clientId: string | number;
  epochMonth: number | null;
  onClose: () => void;
}

export const PlanOTBReportModal: React.FC<PlanOTBReportModalProps> = ({ clientId, epochMonth, onClose }) => (
  <ReportModal onClose={onClose}>
    <PlanOTBReport clientId={clientId} epochMonth={epochMonth} />
  </ReportModal>
);

export default PlanOTBReport;
